import React, { useState, useEffect, useRef } from 'react';
import ScoreSubmissionModal from './components/ScoreSubmissionModal.jsx';
import SimpleAudioManager from './utils/SimpleAudioManager';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './context/AuthContext.tsx'; 
// Replace the lucide-react import with
import { 
  Shield, 
  Network, 
  Cloud, 
  Lock, 
  Target, 
  Rocket, 
  AlertCircle,
  Zap,
  Battery,
  Clock,
  Activity,
  ShieldIcon,
  Heart,
  GitFork,
  Maximize,
  Copy, 
  RotateCcw,
  ShieldCheck,
  RefreshCw,
  Wind,
  Scan,
  Gift
} from './components/icons';
// Player class data
const PLAYER_CLASSES = {
  FIREWALL: {
    name: "Firewall Specialist",
    icon: <Shield color="#f87171" size={24} />,
    color: "#7f1d1d80",
    bgColor: "bg-red-900/50",
    textColor: "text-red-400",
     stats: { health: 120, damage: 40, fireRate: 800, speed: 1.2 },
    specialAbility: "Firewall Barrier",
    description: "Creates protective barriers that damage incoming threats",
    cooldown: 8000,
    range: 15 // Was 30, now reduced by 50%
  },
  AZURE: {
    name: "Azure Specialist",
    icon: <Cloud color="#60a5fa" size={24} />,
    color: "#1e3a8a80",
    bgColor: "bg-blue-900/50",
    textColor: "text-blue-400",
    stats: { health: 90, damage: 50, fireRate: 600, speed: 1.4 },
    specialAbility: "Cloud Burst",
    description: "Deploys cloud resources to attack multiple threats at once",
    cooldown: 10000,
    range: 25 // Was 50, now reduced by 50%
  },
  NETWORK: {
    name: "Network Analyst",
    icon: <Network color="#4ade80" size={24} />,
    color: "#14532d80",
    bgColor: "bg-green-900/50",
    textColor: "text-green-400",
    stats: { health: 100, damage: 35, fireRate: 500, speed: 1.5 },
    specialAbility: "Packet Tracer",
    description: "Reveals hidden threats and slows enemy movement",
    cooldown: 12000,
    range: 22.5 // Was 45, now reduced by 50%
  },
  PENTESTER: {
    name: "Penetration Tester",
    icon: <Lock color="#c084fc" size={24} />,
    color: "#581c8780",
    bgColor: "bg-purple-900/50",
    textColor: "text-purple-400",
   stats: { health: 80, damage: 65, fireRate: 900, speed: 1.0 },
    specialAbility: "Exploit Finder",
    description: "Identifies vulnerabilities for critical damage hits",
    cooldown: 15000,
    range: 17.5 // Was 35, now reduced by 50%
  }
};

// Threat types with enhanced behaviors
const THREAT_TYPES = [
  { 
    name: 'Malware Swarm', 
    color: '#ef4444', 
    bgColor: 'bg-red-900/50', 
    icon: '🦠', 
    health: 100,
    behavior: 'standard',
    damage: 12, // Increased from 10
    resistance: 0 // No damage reduction
  },
  { 
    name: 'DDoS Bot', 
    color: '#3b82f6', 
    bgColor: 'bg-blue-900/50', 
    icon: '⚡', 
    health: 150,
    behavior: 'fast',
    damage: 10, // Increased from 8
    resistance: 0.2 // 20% damage reduction
  },
  { 
    name: 'Phishing Email', 
    color: '#22c55e', 
    bgColor: 'bg-green-900/50', 
    icon: '🎣', 
    health: 75,
    behavior: 'erratic',
    damage: 18, // Increased from 15
    resistance: 0 // No damage reduction
  },
  { 
    name: 'Rootkit', 
    color: '#8b5cf6', 
    bgColor: 'bg-purple-900/50', 
    icon: '🔑', 
    health: 225, // Increased from 200
    behavior: 'stealth',
    damage: 25, // Increased from 20
    resistance: 0.4 // 40% damage reduction
  },
  { 
    name: 'Ransomware', 
    color: '#f59e0b', 
    bgColor: 'bg-amber-900/50', 
    icon: '🔒', 
    health: 140, // Increased from 120
    behavior: 'explosive',
    damage: 15, // Increased from 12
    resistance: 0.1 // 10% damage reduction
  },
  { 
    name: 'OS Bug', 
    color: '#ec4899', 
    bgColor: 'bg-pink-900/50', 
    icon: '🐛', 
    health: 250,
    behavior: 'splitting',
    damage: 20,
    resistance: 0.25, // 25% damage reduction
    size: 1.5, // Larger than normal threats
    splitCount: 3 // Number of mini-bugs it creates when destroyed
  },
  { 
    name: 'Mini Bug', 
    color: '#ec4899', 
    bgColor: 'bg-pink-900/50', 
    icon: '🐞', 
    health: 60,
    behavior: 'fast',
    damage: 8,
    resistance: 0,
    size: 0.7, // Smaller than normal threats
    isSplit: true // Flag to identify split bugs
  },
  { 
  name: 'VLAN Keeper', 
  color: '#06b6d4', // Cyan color
  bgColor: 'bg-cyan-900/50', 
  icon: '🔀', 
  health: 180,
  behavior: 'vlan',
  damage: 15,
  resistance: 0.3, // 30% damage reduction
  size: 1.3,
  createLineInterval: 5000, // Create a new line every 5 seconds
  maxLines: 4 // Maximum number of lines active at once
}
];

// Define the boss
const BOSS_TYPES = [
  {
    name: 'Botnet Master',
    health: 1000,
    size: 4.0,  // 4x normal size
    color: '#cc0000',
    bgColor: 'bg-red-900/70',
    icon: '👾',  // Or another appropriate icon
    damage: 30,
    resistance: 0.5,  // 50% damage reduction
    phases: [
      { healthThreshold: 1.0, behavior: 'shielded' },
      { healthThreshold: 0.75, behavior: 'aggressive' },
      { healthThreshold: 0.5, behavior: 'disruptive' },
      { healthThreshold: 0.25, behavior: 'desperate' }
    ],
    shieldBots: {
      count: 10,
      size: 1.5,
      health: 80,
      damage: 15,
      color: '#aa0000',
      icon: '🔴'
    }
  }
];

// Power-up types
const POWER_UP_TYPES = [
  {
    name: 'Health Boost',
    icon: <Activity size={16} color="#4ade80" />,
    effect: 'health',
    value: 30,
    color: '#4ade80',
    bgColor: 'bg-green-900/50',
    duration: 0  // Instant effect
  },
  {
    name: 'Damage Up',
    icon: <Zap size={16} color="#f87171" />,
    effect: 'damage',
    value: 1.5, // Multiplier
    color: '#f87171', 
    bgColor: 'bg-red-900/50',
    duration: 10000 // 10 seconds
  },
  {
    name: 'Fire Rate Up',
    icon: <Rocket size={16} color="#60a5fa" />,
    effect: 'fireRate',
    value: 0.6, // Multiplier (lower is faster)
    color: '#60a5fa',
    bgColor: 'bg-blue-900/50',
    duration: 8000 // 8 seconds
  },
  {
    name: 'Shield',
    icon: <ShieldIcon size={16} color="#c084fc" />,
    effect: 'shield',
    value: 3, // Blocks 3 hits
    color: '#c084fc',
    bgColor: 'bg-purple-900/50',
    duration: 12000 // 12 seconds
  }
];

// Code snippets for firing
const CODE_SNIPPETS = [
  'if (threat)',
  'return false;',
  'break;',
  'throw new Error();',
  'delete threat;',
  'const null = undefined;',
  'threat.remove();',
  'encrypt(threat);',
  'await patch();',
  'sudo rm threat',
  'firewall.block()',
  'threat === null',
  'antivirus.scan()'
];

// Define all available upgrades in the game
const UPGRADES = {
  // Attack Upgrades - KEEP THESE (they work correctly)
  CODE_PENETRATION: {
    id: 'code_penetration',
    name: 'Code Penetration',
    description: 'Bullets pierce through one additional enemy',
    icon: <Zap size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.bulletPiercing = (stats.bulletPiercing || 0) + 1;
      return stats;
    }
  },
  RECURSIVE_FUNCTION: {
    id: 'recursive_function',
    name: 'Recursive Function',
    description: 'Bullets have 15% chance to split into two when hitting an enemy',
    icon: <GitFork size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.splitChance = (stats.splitChance || 0) + 0.15;
      return stats;
    }
  },
  DEBUG_PROTOCOL: {
    id: 'debug_protocol',
    name: 'Debug Protocol',
    description: '20% chance to land critical hits dealing double damage',
    icon: <Target size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.critChance = (stats.critChance || 0) + 0.2;
      stats.critDamage = 2.0;
      return stats;
    }
  },
  SYNTAX_EXPANSION: {
    id: 'syntax_expansion',
    name: 'Syntax Expansion',
    description: 'Increase bullet size and damage by 25%',
    icon: <Maximize size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 2,
    effect: (stats, level) => {
      stats.bulletSize = (stats.bulletSize || 1) + 0.25;
      stats.damageMultiplier = (stats.damageMultiplier || 1) * 1.25;
      return stats;
    }
  },
  PARALLEL_PROCESSING: {
    id: 'parallel_processing',
    name: 'Parallel Processing',
    description: 'Fire two bullets at once with a slight damage reduction',
    icon: <Copy size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 1,
    effect: (stats, level) => {
      stats.multiShot = (stats.multiShot || 1) + 1;
      stats.damageMultiplier = (stats.damageMultiplier || 1) * 0.85;
      return stats;
    }
  },
  // NEW ATTACK UPGRADE
  EXPLOSIVE_ROUNDS: {
    id: 'explosive_rounds',
    name: 'Explosive Rounds',
    description: 'Bullets create small explosions that damage nearby enemies',
    icon: <Rocket size={16} color="#f87171" />,
    category: 'attack',
    maxLevel: 2,
    effect: (stats, level) => {
      stats.explosiveRadius = (stats.explosiveRadius || 0) + 8;
      stats.explosiveDamage = (stats.explosiveDamage || 0) + 15;
      return stats;
    }
  },

  // Defense Upgrades - KEEP THESE (they work correctly)
  MEMORY_ALLOCATION: {
    id: 'memory_allocation',
    name: 'Memory Allocation',
    description: 'Increase maximum health by 20%',
    icon: <Heart size={16} color="#4ade80" />,
    category: 'defense',
    maxLevel: 3,
    effect: (stats, level) => {
      const healthIncrease = Math.round(stats.baseHealth * 0.2);
      stats.maxHealthBonus = (stats.maxHealthBonus || 0) + healthIncrease;
      return stats;
    }
  },
  CHECKPOINT_SAVE: {
    id: 'checkpoint_save',
    name: 'Checkpoint Save',
    description: 'Auto-revive once with 30% health when killed',
    icon: <RotateCcw size={16} color="#4ade80" />,
    category: 'defense',
    maxLevel: 1,
    effect: (stats, level) => {
      stats.reviveOnce = true;
      return stats;
    }
  },
  VIRTUAL_MACHINE: {
    id: 'virtual_machine',
    name: 'Virtual Machine',
    description: 'Reduce incoming damage by 15%',
    icon: <ShieldCheck size={16} color="#4ade80" />,
    category: 'defense',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.damageReduction = (stats.damageReduction || 0) + 0.15;
      return stats;
    }
  },
  ENCRYPTION_LAYER: {
    id: 'encryption_layer',
    name: 'Encryption Layer',
    description: '10% chance to completely negate incoming damage',
    icon: <Lock size={16} color="#4ade80" />,
    category: 'defense',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.dodgeChance = (stats.dodgeChance || 0) + 0.1;
      return stats;
    }
  },
  BACKUP_SYSTEM: {
    id: 'backup_system',
    name: 'Backup System',
    description: 'Regenerate 1% health every 3 seconds',
    icon: <RefreshCw size={16} color="#4ade80" />,
    category: 'defense',
    maxLevel: 2,
    effect: (stats, level) => {
      stats.healthRegen = (stats.healthRegen || 0) + 0.01;
      stats.healthRegenInterval = 3000;
      return stats;
    }
  },

  // Utility Upgrades - FIXED VERSIONS
  WIDER_SCAN: {
    id: 'wider_scan',
    name: 'Wider Scan',
    description: 'Increase attack range by 25%',
    icon: <Scan size={16} color="#60a5fa" />,
    category: 'utility',
    maxLevel: 3,
    effect: (stats, level) => {
      stats.rangeMultiplier = (stats.rangeMultiplier || 1) * 1.25;
      return stats;
    }
  },
  PROTOCOL_BREACH: {
    id: 'protocol_breach',
    name: 'Protocol Breach',
    description: 'Special ability cooldown reduced by 20%',
    icon: <Zap size={16} color="#60a5fa" />,
    category: 'utility',
    maxLevel: 2,
    effect: (stats, level) => {
      stats.cooldownReduction = (stats.cooldownReduction || 0) + 0.2;
      return stats;
    }
  },
  // NEW UTILITY UPGRADES
  AUTO_TARGETING: {
    id: 'auto_targeting',
    name: 'Smart Targeting',
    description: 'Automatically target the weakest enemy in range',
    icon: <Target size={16} color="#60a5fa" />,
    category: 'utility',
    maxLevel: 1,
    effect: (stats, level) => {
      stats.smartTargeting = true;
      return stats;
    }
  },
  LOOT_MAGNET: {
    id: 'loot_magnet',
    name: 'Loot Magnet',
    description: 'Power-ups are attracted to you from further away',
    icon: <Gift size={16} color="#60a5fa" />,
    category: 'utility',
    maxLevel: 2,
    effect: (stats, level) => {
      stats.lootMagnetRadius = (stats.lootMagnetRadius || 0) + 15;
      return stats;
    }
  },
  THREAT_SCANNER: {
    id: 'threat_scanner',
    name: 'Threat Scanner',
    description: 'Reveals all stealth threats permanently',
    icon: <Activity size={16} color="#60a5fa" />,
    category: 'utility',
    maxLevel: 1,
    effect: (stats, level) => {
      stats.revealStealth = true;
      return stats;
    }
  }
};


// Game constants
const GAME_TICK = 50; // ms per tick
const BASE_THREAT_SPAWN_RATE = 2500; // Increased from 2000ms for easier start
const GAME_DIFFICULTY_SCALE = 0.997; // Changed from 0.995 for more gradual scaling
const POWER_UP_DROP_CHANCE = 0.12; // Increased from 0.1 for more power-ups
const CODE_BULLET_RANGE = 40; // Maximum bullet range in percentage of game area (was effectively 100 before)
const MANUAL_FIRE_COOLDOWN = 300; // ms between manual shots (prevents spamming)
const EXPLOSION_PARTICLES_COUNT = 15; // Number of particles in an explosion
const EXPLOSION_DURATION = 800; // Duration of explosion animation in ms

const BOSS_WARNING_THRESHOLD = 680; // Score at which warning appears
const BOSS_SPAWN_THRESHOLD = 700;   // Score at which boss spawns
 // Placeholder user - replace with actual authentication later
  const user = null; // Or { id: '123', username: 'Player1' } for testing
const CyberGame = () => {
  // Game states
   const { user, profile, isAuthenticated } = useAuth();
  const [gameActive, setGameActive] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showScoreSubmission, setShowScoreSubmission] = useState(false);
const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [threatsDestroyed, setThreatsDestroyed] = useState(0);
  const [autoFire, setAutoFire] = useState(true);
  const [health, setHealth] = useState(100);
  const [specialCooldown, setSpecialCooldown] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [screenShake, setScreenShake] = useState({ x: 0, y: 0 });
const [isPaused, setIsPaused] = useState(false);
const [upgradeChoices, setUpgradeChoices] = useState([]);
const [showUpgradeScreen, setShowUpgradeScreen] = useState(false);
const [upgrades, setUpgrades] = useState([]);
const [lastUpgradeScore, setLastUpgradeScore] = useState(0);
const [upgradeNotification, setUpgradeNotification] = useState(false);
const UPGRADE_SCORE_INTERVAL = 150; // Points between upgrade offers
const [soundEnabled, setSoundEnabled] = useState(true);
const typingSoundRef = useRef(null);
const savedPlayerPosRef = useRef(null);
const [threatsFrozen, setThreatsFrozen] = useState(false);
  
  // Force re-render on game tick
  const [tick, setTick] = useState(0);
  
  // Game elements - using refs to avoid re-renders
  const threatsRef = useRef([]);
  const codeLinesRef = useRef([]);
  const playerPosRef = useRef({ x: 50, y: 50 });
  const gameAreaRef = useRef(null);
  const autoFireTimeRef = useRef(0);
  const lastThreatIdRef = useRef(0);
  const lastCodeLineIdRef = useRef(0);
  const activePowerUpsRef = useRef([]);
  const availablePowerUpsRef = useRef([]);
  const lastPowerUpMilestoneRef = useRef(0);
  const specialCooldownTimeRef = useRef(0);
  const playerStatsRef = useRef({});
  const shieldChargesRef = useRef(0);
  const isSpecialKeyPressedRef = useRef(false);
  const threatSpawnRateRef = useRef(BASE_THREAT_SPAWN_RATE);
  const manualFireTimeRef = useRef(0);
  const explosionParticlesRef = useRef([]);
  const playerDeadRef = useRef(false);
  const vlanBarriersRef = useRef([]);
  const playerPositionHistoryRef = useRef([]);
  // Boss-specific refs
const bossRef = useRef(null);
const bossActiveRef = useRef(false);
const bossFightCompleteRef = useRef(false);
const bossWarningShownRef = useRef(false);
const bossPhaseRef = useRef(0);
const bossShieldBotsRef = useRef([]);
const [warningMessage, setWarningMessage] = useState('');
const [gameAreaClass, setGameAreaClass] = useState('');
const [gridStyle, setGridStyle] = useState({
  backgroundSize: '50px 50px, 50px 50px, 50px 50px, 10px 10px, 10px 10px',
  backgroundImage: 
    "radial-gradient(circle at 25px 25px, rgba(40, 200, 80, 0.2) 2px, transparent 0)," +
    "linear-gradient(to right, rgba(40, 200, 80, 0.1) 1px, transparent 1px)," +
    "linear-gradient(to bottom, rgba(40, 200, 80, 0.1) 1px, transparent 1px)," +
    "linear-gradient(to right, rgba(40, 200, 80, 0.05) 0.5px, transparent 1px)," +
    "linear-gradient(to bottom, rgba(40, 200, 80, 0.05) 0.5px, transparent 1px)",
  backgroundPosition: 'center center',
  backgroundColor: "#0c1221"
});
const controlsHackedRef = useRef(false);
  
  const prepareGameStats = () => {
  return {
    score,
    level,
    threatsDestroyed,
    classType: selectedClass,
    health
  };
};

  // Select player class and start game
// Select player class and start game
// Fix the selectClass function to properly reset all game state when restarting

const selectClass = (classKey) => {
  // Reset player state
  setSelectedClass(classKey);
  setHealth(PLAYER_CLASSES[classKey].stats.health);
  playerDeadRef.current = false; // Reset death state
     
  // Clear all game objects
  threatsRef.current = [];
  codeLinesRef.current = [];
  activePowerUpsRef.current = [];
  availablePowerUpsRef.current = [];
  vlanBarriersRef.current = []; // Important: Clear VLAN barriers
  explosionParticlesRef.current = []; // Clear explosion particles
  playerPositionHistoryRef.current = []; // Clear position history
  
  // Reset game stats
  setScore(0);
  setThreatsDestroyed(0);
  setLevel(1);
  setGameActive(true);
  setSpecialCooldown(0);
  specialCooldownTimeRef.current = 0;
  shieldChargesRef.current = 0;
  threatSpawnRateRef.current = BASE_THREAT_SPAWN_RATE;
  
  // Reset upgrades and related states
  setUpgrades([]);
  setUpgradeChoices([]);
  setLastUpgradeScore(0);
  setShowUpgradeScreen(false);
  setUpgradeNotification(false);
  
  // Reset boss-related state
  resetBossStates();
  
  // Set initial player stats (for power-up modifications)
  playerStatsRef.current = {
    // Base stats
    damage: PLAYER_CLASSES[classKey].stats.damage,
    fireRate: PLAYER_CLASSES[classKey].stats.fireRate,
    speed: PLAYER_CLASSES[classKey].stats.speed,
    baseHealth: PLAYER_CLASSES[classKey].stats.health,
    
    // Upgrade stats (initialized with default values)
    damageMultiplier: 1,
    speedMultiplier: 1,
    rangeMultiplier: 1,
    maxHealthBonus: 0,
    bulletPiercing: 0,
    multiShot: 1,
    splitChance: 0,
    critChance: 0,
    critDamage: 1,
    bulletSize: 1,
    damageReduction: 0,
    dodgeChance: 0,
    healthRegen: 0,
    healthRegenInterval: 0,
    lastRegenTime: 0,
    reviveOnce: false,
    hasRevived: false,
    dropRateMultiplier: 1,
    powerupDurationMultiplier: 1,
    cooldownReduction: 0
  };
  
// Stop any previous typing sound
SimpleAudioManager.stop('typing');
  
// Start typing sound if sound is enabled
if (soundEnabled) {
  // Make sure typing sound is loaded
  if (!SimpleAudioManager.getAudio('typing')) {
    SimpleAudioManager.preloadAudio('typing', '/sounds/typing.mp3')
      .then(() => {
        SimpleAudioManager.play('typing', {
          loop: true,
          volume: 0.2 // Adjust volume to be in the background
        });
      })
      .catch(e => console.log("Audio loading failed:", e));
  } else {
    SimpleAudioManager.play('typing', {
      loop: true,
      volume: 0.2
    });
  }
}
  
  
  // Reset player position to center
  playerPosRef.current = { x: 50, y: 50 };
  
  // Create initial threats
  for (let i = 0; i < 3; i++) {
    createThreat();
  }
};
    
const toggleSound = () => {
  const newSoundState = !soundEnabled;
  setSoundEnabled(newSoundState);
  localStorage.setItem('audio_enabled', newSoundState.toString());
  
  if (newSoundState) {
    // If game is active, start typing sound
    if (gameActive && !isPaused) {
      SimpleAudioManager.play('typing', { 
        loop: true,
        volume: 0.2  // Lower volume for background sound
      });
    }
  } else {
    // Stop typing sound
    SimpleAudioManager.pause('typing');
  }
};
	
	// Add this with your other helper functions
const playExplosionSound = (intensity = 1.0) => {
  if (!soundEnabled) return;
  
  // Adjust volume based on explosion intensity
  SimpleAudioManager.play('explosion', {
    volume: Math.min(0.7, 0.3 * intensity)
  });
};
	
	// Determine which threats are available based on current score
const getAvailableThreatTypes = () => {
  if (score < 100) {
    // Early game: Only basic threats
    return THREAT_TYPES.filter(threat => 
      threat.name === 'Malware Swarm' || 
      threat.name === 'DDoS Bot' || 
      threat.name === 'Phishing Email');
  } else if (score < 250) {
    // Mid game: No VLAN Keepers or OS Bugs yet
    return THREAT_TYPES.filter(threat => 
      threat.name !== 'VLAN Keeper' && 
      threat.name !== 'OS Bug' && 
      !threat.isSplit);
  } else {
    // Late game: All threats available
    return THREAT_TYPES.filter(threat => !threat.isSplit);
  }
};

 // Create a new threat
const createThreat = (specificX, specificY) => {
  let x, y;
  
  if (specificX !== undefined && specificY !== undefined) {
    // Use specified position
    x = specificX;
    y = specificY;
  } else {
    // Original spawn logic at edges
    const side = Math.floor(Math.random() * 4);
    
    switch (side) {
      case 0: // top
        x = Math.random() * 100;
        y = 0;
        break;
      case 1: // right
        x = 100;
        y = Math.random() * 100;
        break;
      case 2: // bottom
        x = Math.random() * 100;
        y = 100;
        break;
      default: // left
        x = 0;
        y = Math.random() * 100;
    }
  }
  
  const availableThreats = getAvailableThreatTypes();
  const threatType = availableThreats[Math.floor(Math.random() * availableThreats.length)];
  lastThreatIdRef.current++;
  
  // Scale health based on level
  const scaledHealth = Math.floor(threatType.health * (1 + (level - 1) * 0.25));
  const size = threatType.size || 1; // Default size multiplier is 1
  
  const newThreat = {
    id: lastThreatIdRef.current,
    type: threatType.name,
    color: threatType.color,
    bgColor: threatType.bgColor,
    icon: threatType.icon,
    health: scaledHealth,
    maxHealth: scaledHealth,
    x,
    y,
    behavior: threatType.behavior,
    damage: threatType.damage,
    moveTime: 0,
    moveDirection: Math.random() * 2 * Math.PI, // For erratic movement
    visible: threatType.behavior === 'stealth' ? false : true, // FIX: Explicitly set visible property
    size: size, // Use the size property
    splitCount: threatType.splitCount || 0, // Add split count
    isSplit: threatType.isSplit || false, // Flag for mini bugs
    resistance: threatType.resistance || 0
  };
  
  // Apply VLAN behavior if applicable
  if (threatType.behavior === 'vlan') {
    newThreat.createLineInterval = threatType.createLineInterval;
    newThreat.maxLines = threatType.maxLines;
    newThreat.lastLineCreated = Date.now(); // Set initial creation time
  }
  
  threatsRef.current.push(newThreat);
};

// Add this to your component code
// Fix 1: Update how the score update trigger works
const updateScoreReliably = (points) => {
  // Skip score updates if player is dead
  if (health <= 0 || playerDeadRef.current) {

    return;
  }
  
  setScore(prevScore => {
    const newScore = prevScore + points;
    
    // Check if player reached an upgrade milestone
    // Only check when crossing a threshold
    if (Math.floor(newScore / UPGRADE_SCORE_INTERVAL) > 
        Math.floor(lastUpgradeScore / UPGRADE_SCORE_INTERVAL)) {
     
      setUpgradeNotification(true);
      
      // Show upgrade options with a delay to let player see the notification
      setTimeout(() => {
        showUpgradeOptions();
        setUpgradeNotification(false);
      }, 1500);
      
      setLastUpgradeScore(newScore);
    }
  
    // Check boss thresholds separately to avoid redundant checks
    checkBossThresholds(newScore);
    
    return newScore;
  });
};

// Fix 2: Create a separate function to check boss thresholds only when needed
const checkBossThresholds = (score) => {
  // Boss warning phase
  if (score >= BOSS_WARNING_THRESHOLD && score < BOSS_SPAWN_THRESHOLD && !bossWarningShownRef.current) {

    showBossWarning();
    bossWarningShownRef.current = true;
  }
  
  // Boss spawn phase
  if (score >= BOSS_SPAWN_THRESHOLD && !bossActiveRef.current && !bossFightCompleteRef.current) {
 
    spawnBossEncounter();
  }
};

const setHealthWithDeathCheck = (newHealthOrFunction) => {
  setHealth(currentHealth => {
    // Handle both direct value and function updates
    const nextHealth = typeof newHealthOrFunction === 'function' 
      ? newHealthOrFunction(currentHealth) 
      : newHealthOrFunction;
    
    // Check if this update would cause player death
    if (nextHealth <= 0 && !playerDeadRef.current) {

      
      // Check for revival upgrade
      if (playerStatsRef.current.reviveOnce && !playerStatsRef.current.hasRevived) {
     
        playerStatsRef.current.hasRevived = true;
        
        // Calculate revival health (30% of max)
        const maxHealth = PLAYER_CLASSES[selectedClass].stats.health + 
                          (playerStatsRef.current.maxHealthBonus || 0);
        const reviveHealth = Math.ceil(maxHealth * 0.3);
        
        // Create revival effect
        createExplosionParticles(
          playerPosRef.current.x, 
          playerPosRef.current.y, 
          '#4ade80', // Green
          2.5
        );
        
        // Add revival notification
        codeLinesRef.current.push({
          id: Date.now() + Math.random(),
          x: playerPosRef.current.x,
          y: playerPosRef.current.y,
          targetX: playerPosRef.current.x,
          targetY: playerPosRef.current.y - 15,
          text: "CHECKPOINT REVIVAL",
          isSpecial: true,
          ttl: 100
        });
        
        return reviveHealth;
      }
      
      playerDeadRef.current = true;
      
      // Create death explosion effect
      createExplosionParticles(
        playerPosRef.current.x, 
        playerPosRef.current.y, 
        PLAYER_CLASSES[selectedClass]?.color || "#3b82f680", 
        2.0
      );
      
      // Add a "GAME OVER" text effect
      codeLinesRef.current.push({
        id: Date.now() + Math.random(),
        x: playerPosRef.current.x,
        y: playerPosRef.current.y,
        targetX: playerPosRef.current.x,
        targetY: playerPosRef.current.y - 10,
        text: "GAME OVER",
        isScore: true,
        ttl: 100 // Stay on screen longer
      });
      
      // Cancel auto-fire
      setAutoFire(true);
      
      return 0;
    }
    
    return Math.max(0, nextHealth);
  });
};

// Function to generate random upgrade options
const generateRandomUpgrades = (count) => {
  // Get all available upgrades that haven't reached max level
  const availableUpgrades = Object.values(UPGRADES).filter(upgrade => {
    const currentLevel = upgrades.filter(u => u.id === upgrade.id).length;
    return currentLevel < upgrade.maxLevel;
  });
  
  // Shuffle available upgrades
  const shuffled = [...availableUpgrades].sort(() => 0.5 - Math.random());
  
  // Take the first 'count' upgrades, or all if there are fewer than 'count'
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

// Function to display upgrade options
const showUpgradeOptions = () => {
  // Only show if we have upgrades available and player is alive
  if (health <= 0 || playerDeadRef.current) return;
    
  // Generate 3 random unique upgrades from the available pool
  const choices = generateRandomUpgrades(3);
    
  if (choices.length === 0) {

    return;
  }
  
  // Save current player position before showing upgrade screen
  savedPlayerPosRef.current = { ...playerPosRef.current };
  
  setUpgradeChoices(choices);
  setShowUpgradeScreen(true);
  setUpgradeNotification(false); // Clear the notification when showing options
  setIsPaused(true); 
};

// Modify the selectUpgrade function
const selectUpgrade = (upgrade) => {

  
  // Add the upgrade to player's upgrades collection
  setUpgrades(prev => [...prev, upgrade]);
  
  // Apply the upgrade effect
  applyUpgradeEffect(upgrade);
  
  // Close upgrade screen and resume game
  setShowUpgradeScreen(false);
  setIsPaused(false);
  
  // Restore the saved player position
  if (savedPlayerPosRef.current) {
    playerPosRef.current = savedPlayerPosRef.current;
    savedPlayerPosRef.current = null; // Clear saved position
  }
  
  // Create visual effect to show upgrade was applied
  createExplosionParticles(
    playerPosRef.current.x, 
    playerPosRef.current.y, 
    '#FFD700', 
    1.5
  );
  
  // Add floating text to show upgrade acquired
  codeLinesRef.current.push({
    id: Date.now() + Math.random(),
    x: playerPosRef.current.x,
    y: playerPosRef.current.y,
    targetX: playerPosRef.current.x,
    targetY: playerPosRef.current.y - 15,
    text: `UPGRADE: ${upgrade.name}`,
    isSpecial: true,
    ttl: 80 // Stay on screen longer
  });
};

// Function to apply upgrade effects
const applyUpgradeEffect = (upgrade) => {
  // Get the current level of this upgrade (how many times it's been selected)
  const currentLevel = upgrades.filter(u => u.id === upgrade.id).length + 1;
  
  // Apply the effect by updating playerStatsRef
  playerStatsRef.current = upgrade.effect(playerStatsRef.current, currentLevel);
  
  // Handle special cases that need immediate state updates
  if (upgrade.id === 'memory_allocation') {
    // Increase current health along with max health
    const healthIncrease = Math.round(PLAYER_CLASSES[selectedClass].stats.health * 0.2);
    setHealthWithDeathCheck(h => Math.min(
      h + healthIncrease, 
      PLAYER_CLASSES[selectedClass].stats.health + playerStatsRef.current.maxHealthBonus
    ));
  }
  

};

// Create a VLAN barrier line
const createVLANBarrier = (vlanKeeper) => {
  // Determine line orientation (horizontal or vertical)
  const isHorizontal = Math.random() > 0.5;
  
  // Create line that doesn't go through the player (to avoid instant damage)
  const playerPos = playerPosRef.current;
  let x1, y1, x2, y2;
  
  if (isHorizontal) {
    // Horizontal line
    y1 = y2 = Math.random() * 100;
    // Avoid creating line too close to player
    if (Math.abs(y1 - playerPos.y) < 10) {
      y1 = y2 = (playerPos.y + 15) % 100; // Offset from player
    }
    x1 = 0;
    x2 = 100;
  } else {
    // Vertical line
    x1 = x2 = Math.random() * 100;
    // Avoid creating line too close to player
    if (Math.abs(x1 - playerPos.x) < 10) {
      x1 = x2 = (playerPos.x + 15) % 100; // Offset from player
    }
    y1 = 0;
    y2 = 100;
  }
  
  vlanBarriersRef.current.push({
    id: 'vlan-' + Date.now() + Math.random(),
    vlanKeeperId: vlanKeeper.id,
    x1, y1, x2, y2,
    isHorizontal,
    createdAt: Date.now(),
    color: vlanKeeper.color,
    damage: vlanKeeper.damage,
    active: true,
    pulsePhase: 0 // For visual pulsing effect
  });
  
  // Create a visual effect at line creation
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  createExplosionParticles(midX, midY, vlanKeeper.color, 0.6);
};

// Check for player collision with VLAN barriers
const checkVLANBarrierCollisions = () => {
  if (health <= 0 || playerDeadRef.current) return;
  
  const playerPos = playerPosRef.current;
  
  // Use more positions to increase detection accuracy
  const positions = [
    ...playerPositionHistoryRef.current,
    playerPos
  ];
  
  // Check if we have at least 2 positions to work with
  if (positions.length < 2) return;
  
  vlanBarriersRef.current.forEach(barrier => {
    if (!barrier.active) return;
    
    // Check for collisions with each pair of consecutive positions
    for (let i = 0; i < positions.length - 1; i++) {
      const prevPos = positions[i];
      const currentPos = positions[i + 1];
      
      const collided = detectLineCollision(
        prevPos.x, prevPos.y, 
        currentPos.x, currentPos.y,
        barrier.x1, barrier.y1, 
        barrier.x2, barrier.y2
      );
      
      if (collided) {
        console.log("[VLAN] Barrier collision detected!");
        
        // Take damage when crossing a barrier
        if (shieldChargesRef.current > 0) {
          shieldChargesRef.current--;
          
          // Update shield power-up display
          const shieldPowerUp = activePowerUpsRef.current.find(p => p.effect === 'shield');
          if (shieldPowerUp) {
            shieldPowerUp.charges = shieldChargesRef.current;
          }
          
          // Remove the shield if no charges left
          if (shieldChargesRef.current === 0) {
            activePowerUpsRef.current = activePowerUpsRef.current.filter(p => p.effect !== 'shield');
          }
        } else {
          setHealthWithDeathCheck(h => Math.max(0, h - barrier.damage));
        }
        
        // Create a visual effect at collision point
        const collisionX = (prevPos.x + currentPos.x) / 2;
        const collisionY = (prevPos.y + currentPos.y) / 2;
        createExplosionParticles(collisionX, collisionY, barrier.color, 0.8);
        
        // Create floating text effect
        codeLinesRef.current.push({
          id: Date.now() + Math.random(),
          x: collisionX,
          y: collisionY,
          targetX: collisionX,
          targetY: collisionY - 10,
          text: 'VLAN BREACH',
          damage: 0,
          isResist: true,
          ttl: 30
        });
        
        // Push player back to previous position but with a bit of offset to prevent getting stuck
        const angle = Math.atan2(playerPos.y - collisionY, playerPos.x - collisionX);
        const pushbackDistance = 5;
        playerPosRef.current = { 
          x: prevPos.x + Math.cos(angle) * pushbackDistance,
          y: prevPos.y + Math.sin(angle) * pushbackDistance
        };
        
        // Clear position history to prevent multiple collision detections
        playerPositionHistoryRef.current = [];
        
        // Exit the loop after first collision
        return;
      }
    }
  });
};

// Line segment intersection detection function
const detectLineCollision = (x1, y1, x2, y2, x3, y3, x4, y4) => {
  // Add some thickness to the lines to make collision detection more forgiving
  const thickness = 2.0;
  
  // Calculate the direction vectors
  const dx1 = x2 - x1;
  const dy1 = y2 - y1;
  const dx2 = x4 - x3;
  const dy2 = y4 - y3;
  
  // Calculate the determinant
  const determinant = dx1 * dy2 - dy1 * dx2;
  
  // Check if lines are nearly parallel
  if (Math.abs(determinant) < 0.001) {
    // For nearly parallel lines, check if they're close enough
    // (this is a simplified check)
    const dist = Math.abs((y3 - y1) * dx1 - (x3 - x1) * dy1) / 
             Math.sqrt(dx1 * dx1 + dy1 * dy1);
    return dist < thickness;
  }
  
  // Calculate parameters for the intersection point
  const t1 = ((x3 - x1) * dy2 - (y3 - y1) * dx2) / determinant;
  const t2 = ((x1 - x3) * dy1 - (y1 - y3) * dx1) / -determinant;
  
  // Check if the intersection is within both line segments, with some tolerance
  return (t1 >= -0.1 && t1 <= 1.1 && t2 >= -0.1 && t2 <= 1.1);
};

// Helper function to find threats near a position
const findNearbyThreats = (x, y, maxCount) => {
  // Only consider visible threats
  const visibleThreats = threatsRef.current.filter(threat => threat.visible);
  
  // Sort threats by distance to this position
  const sortedThreats = visibleThreats
    .map(threat => {
      const dx = threat.x - x;
      const dy = threat.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return { ...threat, distance };
    })
    .sort((a, b) => a.distance - b.distance);
  
  // Return the closest 'maxCount' threats
  return sortedThreats.slice(0, maxCount);
};

// Helper function to find a threat in the specified direction
const findThreatInDirection = (startX, startY, dirX, dirY) => {
  // Normalize direction vector
  const length = Math.sqrt(dirX * dirX + dirY * dirY);
  const normDirX = dirX / length;
  const normDirY = dirY / length;
  
  // Get the current class's range
  const playerRange = selectedClass ? 
    PLAYER_CLASSES[selectedClass].range * (playerStatsRef.current.rangeMultiplier || 1) : 
    CODE_BULLET_RANGE;
  
  // Loop through threats to find one that's in our direction
  return threatsRef.current.find(threat => {
    // Only consider visible threats
    if (!threat.visible) return false;
    
    // Calculate vector from start to threat
    const threatDx = threat.x - startX;
    const threatDy = threat.y - startY;
    const threatDist = Math.sqrt(threatDx * threatDx + threatDy * threatDy);
    
    // Check if threat is within range
    if (threatDist > playerRange) return false;
    
    // Calculate dot product to check if threat is in our direction
    const dotProduct = (normDirX * threatDx + normDirY * threatDy) / threatDist;
    
    // Threat is in our direction if the dot product is close to 1
    return dotProduct > 0.7;
  });
};


const effectivePaused = isPaused || showUpgradeScreen;

// Pause function
const togglePause = () => {
  // Don't allow manual pausing when upgrade screen is showing
  if (showUpgradeScreen) return;
  
  const newPauseState = !isPaused;
  setIsPaused(newPauseState);
  
  // Pause/resume typing sound
  if (soundEnabled) {
    if (newPauseState) {
      SimpleAudioManager.pause('typing');
    } else {
      SimpleAudioManager.resume('typing');
    }
  }
};
 // Handle threat destruction and potential splitting
const handleThreatDestroyed = (threat) => {
	  // Check if player is dead before processing
  if (health <= 0 || playerDeadRef.current) {
    
    // Still create explosion effect for visual purposes
    createExplosionParticles(threat.x, threat.y, threat.color, 0.7);
    // Just remove the threat from the array without updating score
    const threatIndex = threatsRef.current.findIndex(t => t.id === threat.id);
    if (threatIndex > -1) {
      threatsRef.current.splice(threatIndex, 1);
    }
    return;
  }
  // Calculate explosion intensity based on threat type/size
  let explosionIntensity = 1.0;
  
  // Larger threats have bigger explosions
  if (threat.size) {
    explosionIntensity *= threat.size;
  }
  
  // Special threats have more intense explosions
  if (threat.behavior === 'explosive') {
    explosionIntensity *= 1.5;
  } else if (threat.behavior === 'splitting') {
    explosionIntensity *= 1.3;
  } else if (threat.behavior === 'stealth') {
    explosionIntensity *= 1.2;
  }
  
  // Mini bugs have smaller explosions
  if (threat.isSplit) {
    explosionIntensity *= 0.6;
  }
  
  // Create explosion particles with appropriate intensity
  createExplosionParticles(threat.x, threat.y, threat.color, explosionIntensity);
  
  playExplosionSound(explosionIntensity);
  
  // Check for explosive rounds upgrade
if (playerStatsRef.current.explosiveRadius > 0) {
  const explosiveRadius = playerStatsRef.current.explosiveRadius;
  const explosiveDamage = playerStatsRef.current.explosiveDamage;
  
  // Create larger explosion effect for explosive rounds
  createExplosionParticles(threat.x, threat.y, '#ff6b35', explosionIntensity * 1.5);
  
  // Damage nearby threats
  threatsRef.current.forEach(nearbyThreat => {
    if (nearbyThreat.id !== threat.id) {
      const dx = threat.x - nearbyThreat.x;
      const dy = threat.y - nearbyThreat.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < explosiveRadius) {
        // Apply explosive damage
        const resistanceFactor = 1 - (nearbyThreat.resistance || 0);
        const actualDamage = Math.floor(explosiveDamage * resistanceFactor);
        nearbyThreat.health -= actualDamage;
        
        // Create visual effect for explosion damage
        codeLinesRef.current.push({
          id: Date.now() + Math.random(),
          x: nearbyThreat.x,
          y: nearbyThreat.y,
          targetX: nearbyThreat.x + (Math.random() * 8 - 4),
          targetY: nearbyThreat.y + (Math.random() * 8 - 4),
          text: 'BOOM!',
          isSpecial: true,
          ttl: 25
        });
      }
    }
  });
}
  
  // Drop power-up with certain probability
  if (Math.random() < POWER_UP_DROP_CHANCE) {
    createPowerUp(threat.x, threat.y);
  }
  
  // Add floating score text
  codeLinesRef.current.push({
    id: Date.now() + Math.random(),
    x: threat.x,
    y: threat.y,
    targetX: threat.x,
    targetY: threat.y - 10, // Float upward
    text: `+${threat.isSplit ? 5 : 10}`,
    damage: 0,
    isScore: true,
    ttl: 50
  });
   
  // Check if this is a splitting threat
  if (threat.behavior === 'splitting' && threat.splitCount > 0) {
    // ... [rest of the original splitting code]
  }
  
  if (threat.behavior === 'vlan') {
  // Create a larger explosion when VLAN Keeper is destroyed
  createExplosionParticles(threat.x, threat.y, threat.color, 1.8);
    
  // Clear all VLAN barriers created by this keeper
  vlanBarriersRef.current = vlanBarriersRef.current.filter(barrier => {
    if (barrier.vlanKeeperId === threat.id) {
      // Create small explosion effect at barrier midpoints
      const midX = (barrier.x1 + barrier.x2) / 2;
      const midY = (barrier.y1 + barrier.y2) / 2;
      createExplosionParticles(midX, midY, threat.color, 0.4);
      return false; // Remove this barrier
    }
    return true; // Keep barriers from other VLAN Keepers
  });
}
  
  // Remove the original threat
  const threatIndex = threatsRef.current.findIndex(t => t.id === threat.id);
  if (threatIndex > -1) {
    threatsRef.current.splice(threatIndex, 1);
  }
  
  updateScoreReliably(threat.isSplit ? 5 : 10);
  setThreatsDestroyed(t => t + 1);
  // Adjust threat spawn rate based on threats destroyed
  threatSpawnRateRef.current = Math.max(
    500, // Minimum spawn time
    BASE_THREAT_SPAWN_RATE * Math.pow(GAME_DIFFICULTY_SCALE, threatsDestroyed)
  );
};
  
  // Handle power up drops
  const createPowerUp = (x, y) => {
    // Choose a random power-up type
    const powerUpType = POWER_UP_TYPES[Math.floor(Math.random() * POWER_UP_TYPES.length)];
    
    availablePowerUpsRef.current.push({
      id: Date.now() + Math.random(),
      type: powerUpType.name,
      effect: powerUpType.effect,
      value: powerUpType.value,
      duration: powerUpType.duration,
      color: powerUpType.color,
      bgColor: powerUpType.bgColor,
      icon: powerUpType.icon,
      x,
      y,
      createdAt: Date.now(),
      expiresAt: Date.now() + 10000 // Power-ups disappear after 10 seconds
    });
  };
  
  // Collect a power-up
// The problematic code in collectPowerUp is:
// case 'health':
//   setHealthWithDeathCheck(h => Math.max(0, h - threat.damage));
//       PLAYER_CLASSES[selectedClass].stats.health, 
//       h + powerUp.value
//     ));
//     break;

// Here's the fixed version:
const collectPowerUp = (powerUp) => {
  // Apply power-up effect
  switch (powerUp.effect) {
    case 'health':
      // Don't resurrect player with health powerups if they're already dead
      if (!playerDeadRef.current) {
        setHealthWithDeathCheck(h => Math.min(
          PLAYER_CLASSES[selectedClass].stats.health, 
          h + powerUp.value
        ));
      }
      break;
      
    case 'damage':
    case 'fireRate':
      // For temporary effects, add to active power-ups
      if (powerUp.duration > 0) {
        activePowerUpsRef.current.push({
          id: powerUp.id,
          name: powerUp.type,
          effect: powerUp.effect,
          value: powerUp.value,
          duration: powerUp.duration,
          startTime: Date.now(),
          expiresAt: Date.now() + powerUp.duration,
          color: powerUp.color,
          icon: powerUp.icon
        });
        
        // Update player stats temporarily
        if (powerUp.effect === 'damage') {
          playerStatsRef.current.damage *= powerUp.value;
        } else if (powerUp.effect === 'fireRate') {
          playerStatsRef.current.fireRate *= powerUp.value;
        }
      }
      break;
      
    case 'shield':
      shieldChargesRef.current = powerUp.value;
      
      activePowerUpsRef.current.push({
        id: powerUp.id,
        name: powerUp.type,
        effect: powerUp.effect,
        value: powerUp.value,
        charges: powerUp.value,
        duration: powerUp.duration,
        startTime: Date.now(),
        expiresAt: Date.now() + powerUp.duration,
        color: powerUp.color,
        icon: powerUp.icon
      });
      break;
      
    default:
 
  }
  
  // Remove from available power-ups
  availablePowerUpsRef.current = availablePowerUpsRef.current.filter(p => p.id !== powerUp.id);
};
 const findNearestThreat = (x, y) => {
  if (threatsRef.current.length === 0) return null;
  
  let nearest = null;
  let shortestDistance = Infinity;
  
  // Get the current class's range with upgrades applied
  const baseRange = selectedClass ? PLAYER_CLASSES[selectedClass].range : CODE_BULLET_RANGE;
  const playerRange = baseRange * (playerStatsRef.current.rangeMultiplier || 1);
  
  // Check for smart targeting upgrade
  const hasSmartTargeting = playerStatsRef.current.smartTargeting;
  
  threatsRef.current.forEach(threat => {
    // Only target visible threats (unless we have threat scanner upgrade)
    const canSee = threat.visible || playerStatsRef.current.revealStealth;
    if (!canSee) return;
    
    const dx = x - threat.x;
    const dy = y - threat.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Only consider threats within the player's range
    if (distance < playerRange) {
      if (hasSmartTargeting) {
        // Smart targeting: prioritize by lowest health, then by distance
        const healthRatio = threat.health / threat.maxHealth;
        const priority = healthRatio + (distance / playerRange) * 0.3; // Health matters more than distance
        
        if (priority < shortestDistance) {
          shortestDistance = priority;
          nearest = { ...threat, distance };
        }
      } else {
        // Normal targeting: closest threat
        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearest = { ...threat, distance };
        }
      }
    }
  });
  
  return nearest;
};
  
  // Create explosion particles
const createExplosionParticles = (x, y, color, intensity = 1) => {
  const particleCount = Math.floor((EXPLOSION_PARTICLES_COUNT + Math.floor(Math.random() * 5)) * intensity);
  const newParticles = [];
  
  // Apply screen shake based on intensity
  const shakeAmount = 3 * intensity;
  setScreenShake({
    x: (Math.random() - 0.5) * shakeAmount,
    y: (Math.random() - 0.5) * shakeAmount
  });
  
  // Reset screen shake after a short delay
  setTimeout(() => {
    setScreenShake({ x: 0, y: 0 });
  }, 150);
  
  for (let i = 0; i < particleCount; i++) {
    // Create particles that go in random directions
    const angle = Math.random() * Math.PI * 2;
    const speed = (0.5 + Math.random() * 2) * intensity;
    const size = (2 + Math.random() * 6) * Math.sqrt(intensity);
    const life = EXPLOSION_DURATION * (0.7 + Math.random() * 0.6);
    
    // Create different particle types
    const particleType = Math.floor(Math.random() * 5);
    let text = '';
    
    switch(particleType) {
      case 0:
        text = '0';
        break;
      case 1:
        text = '1';
        break;
      case 2:
        text = '{';
        break;
      case 3:
        text = '}';
        break;
      case 4:
        text = '#';
        break;
      default:
        text = '*';
    }
    
    newParticles.push({
      id: Date.now() + Math.random(),
      x,
      y,
      velX: Math.cos(angle) * speed,
      velY: Math.sin(angle) * speed,
      size,
      color,
      opacity: 1,
      createdAt: Date.now(),
      life,
      text,
      rotation: Math.random() * 360
    });
  }
  
  // Add ring explosion (multiple rings for higher intensity)
  const ringCount = Math.max(1, Math.floor(intensity));
  for (let i = 0; i < ringCount; i++) {
    const delay = i * 100;
    const ringSize = 20 + (i * 10);
    
    newParticles.push({
      id: Date.now() + Math.random() + i,
      x,
      y,
      size: 1,
      maxSize: ringSize * intensity,
      isRing: true,
      color,
      opacity: 0.8,
      createdAt: Date.now() + delay,
      life: EXPLOSION_DURATION * 0.8
    });
  }
  
  explosionParticlesRef.current = [
    ...explosionParticlesRef.current,
    ...newParticles
  ];
};

// Reset all boss-related states when game restarts
const resetBossStates = () => {
  bossRef.current = null;
  bossActiveRef.current = false;
  bossFightCompleteRef.current = false;
  bossWarningShownRef.current = false;
  bossPhaseRef.current = 0;
  bossShieldBotsRef.current = [];
  setWarningMessage('');
  setGameAreaClass('');
  setGridStyle({
    backgroundSize: '40px 40px',
    backgroundImage: 'linear-gradient(to right, rgba(60, 100, 177, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(60, 100, 177, 0.1) 1px, transparent 1px)',
    backgroundPosition: 'center center'
  });
};

// Check for boss trigger conditions
const checkBossTrigger = () => {
  // Only run checks if game is active and player is alive
  if (!gameActive || health <= 0) {
        return;
  }
  

  
  // Boss warning phase
  const shouldShowWarning = score >= BOSS_WARNING_THRESHOLD && score < BOSS_SPAWN_THRESHOLD && !bossWarningShownRef.current;
  if (shouldShowWarning) {

    showBossWarning();
    bossWarningShownRef.current = true;
    
    // Force a re-render immediately
    setTick(prevTick => prevTick + 100);
  }
  
  // Boss spawn phase
  const shouldSpawnBoss = score >= BOSS_SPAWN_THRESHOLD && !bossActiveRef.current && !bossFightCompleteRef.current;
  if (shouldSpawnBoss) {
    console.log("[Boss System] SPAWNING BOSS!");
    spawnBossEncounter();
    
    // Force a re-render immediately
    setTick(prevTick => prevTick + 200);
  }
};

// Show warning before boss appears
const showBossWarning = () => {

  
  // Set warning message with a callback to ensure execution
  setWarningMessage(prevMsg => {
  
    return "WARNING: Botnet Master detected in the system!";
  });
  
  // Visual changes
  setGameAreaClass("warning-flash");
  setGridStyle({
    backgroundSize: '40px 40px',
    backgroundImage: 'linear-gradient(to right, rgba(220, 38, 38, 0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(220, 38, 38, 0.2) 1px, transparent 1px)',
    backgroundPosition: 'center center'
  });
  
  // Spawn scout bots
  spawnBossScouts();
  
  // Force immediate re-render
  setTick(prevTick => prevTick + 100);
};

// Spawn scout mini-threats as precursors
const spawnBossScouts = () => {
  for (let i = 0; i < 3; i++) {
    const side = Math.floor(Math.random() * 4);
    let x, y;
    
    // Position scouts at the edges
    switch (side) {
      case 0: // top
        x = Math.random() * 100;
        y = 0;
        break;
      case 1: // right
        x = 100;
        y = Math.random() * 100;
        break;
      case 2: // bottom
        x = Math.random() * 100;
        y = 100;
        break;
      default: // left
        x = 0;
        y = Math.random() * 100;
    }
    
    // Create scout bot (similar to mini-bugs)
    threatsRef.current.push({
      id: 'scout-' + Date.now() + Math.random(),
      type: 'Botnet Scout',
      color: '#cc0000',
      bgColor: 'bg-red-900/50',
      icon: '🔴',
      health: 60,
      maxHealth: 60,
      x,
      y,
      behavior: 'fast',
      damage: 8,
      moveTime: 0,
      visible: true,
      size: 0.7,
      resistance: 0
    });
  }
};

// Clear existing threats with an effect
const clearExistingThreats = () => {
  // Create explosion effects for each threat as they're removed
  threatsRef.current.forEach(threat => {
    createExplosionParticles(threat.x, threat.y, threat.color, 0.7);
  });
  
  // Clear threats
  threatsRef.current = [];
};

// Spawn the boss encounter
const spawnBossEncounter = () => {
  setWarningMessage(''); 
  clearExistingThreats();
  
  // FIX: Clear VLAN barriers
  vlanBarriersRef.current = [];
  
  // Set environment to boss mode
  setGameAreaClass("boss-encounter");
  setGridStyle({ 
    backgroundSize: '40px 40px',
    backgroundImage: 'linear-gradient(to right, rgba(220, 38, 38, 0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(220, 38, 38, 0.3) 1px, transparent 1px)',
    backgroundPosition: 'center center'
  });
  
  // Create boss entity
  const bossType = BOSS_TYPES[0];  // Botnet Master
  
  const boss = {
    id: 'boss-' + Date.now(),
    type: bossType.name,
    health: bossType.health,
    maxHealth: bossType.health,
    color: bossType.color,
    bgColor: bossType.bgColor,
    icon: bossType.icon,
    size: bossType.size,
    damage: bossType.damage,
    resistance: bossType.resistance,
    x: 50,  // Center of screen
    y: 20,  // Top area
    phase: 0,
    phaseData: bossType.phases,
    lastAttackTime: 0,
    visible: true,
    isBoss: true
  };
  
  // Store the boss
  bossRef.current = boss;
  threatsRef.current.push(boss);
  bossActiveRef.current = true;
  
  // Spawn shield bots
  spawnShieldBots(boss, bossType.shieldBots);
};

// Spawn shield bots around the boss
const spawnShieldBots = (boss, shieldConfig) => {
  const shieldBots = [];
  
  for (let i = 0; i < shieldConfig.count; i++) {
    // Calculate position in orbit around boss
    const angle = (i / shieldConfig.count) * 2 * Math.PI;
    const orbitRadius = 15;  // Distance from boss center
    
    const shieldBot = {
      id: 'shield-' + i + '-' + Date.now(),
      bossId: boss.id,
      type: 'Shield Bot',
      x: boss.x + Math.cos(angle) * orbitRadius,
      y: boss.y + Math.sin(angle) * orbitRadius,
      angle: angle,
      orbitRadius: orbitRadius,
      orbitSpeed: 0.01,  // How fast it orbits
      health: shieldConfig.health,
      maxHealth: shieldConfig.health,
      color: shieldConfig.color,
      bgColor: 'bg-red-800/50',
      icon: shieldConfig.icon,
      size: shieldConfig.size,
      damage: shieldConfig.damage,
      resistance: 0.3,
      state: 'orbit',  // or 'attack' when rushing player
      visible: true,
      isShieldBot: true
    };
    
    shieldBots.push(shieldBot);
    threatsRef.current.push(shieldBot);
  }
  
  bossShieldBotsRef.current = shieldBots;
};

// Update boss position based on current phase
const updateBossPosition = (boss, deltaTime, speed) => {
  // Calculate movement toward player but with some randomness
  const playerPos = playerPosRef.current;
  const dx = playerPos.x - boss.x;
  const dy = playerPos.y - boss.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  if (distance > 0) {
    // Add some randomness to movement
    const randomAngle = (Math.random() - 0.5) * 0.3; // Small random angle
    const moveAngle = Math.atan2(dy, dx) + randomAngle;
    
    boss.x += Math.cos(moveAngle) * speed;
    boss.y += Math.sin(moveAngle) * speed;
  }
};

// Update shield bots positions
const updateShieldBots = (boss, deltaTime) => {
  bossShieldBotsRef.current.forEach((bot, index) => {
    if (bot.state === 'orbit') {
      // Update orbit angle
      bot.angle += bot.orbitSpeed;
      
      // Update position based on boss position and angle
      bot.x = boss.x + Math.cos(bot.angle) * bot.orbitRadius;
      bot.y = boss.y + Math.sin(bot.angle) * bot.orbitRadius;
      
      // Find the bot in the threats array and update
      const threatIndex = threatsRef.current.findIndex(t => t.id === bot.id);
      if (threatIndex !== -1) {
        threatsRef.current[threatIndex].x = bot.x;
        threatsRef.current[threatIndex].y = bot.y;
      }
    }
    else if (bot.state === 'attack') {
      // Move toward player when attacking
      const playerPos = playerPosRef.current;
      const dx = playerPos.x - bot.x;
      const dy = playerPos.y - bot.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        bot.x += (dx / distance) * 1.2; // Move faster when attacking
        bot.y += (dy / distance) * 1.2;
        
        // Update in threats array
        const threatIndex = threatsRef.current.findIndex(t => t.id === bot.id);
        if (threatIndex !== -1) {
          threatsRef.current[threatIndex].x = bot.x;
          threatsRef.current[threatIndex].y = bot.y;
        }
        
        // Check if reached player - send back to orbit
        if (distance < 5) {
          bot.state = 'returnToOrbit';
          bot.returnTime = Date.now();
        }
      }
    }
    else if (bot.state === 'returnToOrbit') {
      // Return to orbit after attack
      if (Date.now() - bot.returnTime > 1000) {
        bot.state = 'orbit';
      }
    }
  });
};

// Handle boss phase transition
const handlePhaseTransition = (boss) => {
  // Create visual effect for phase change
  createExplosionParticles(boss.x, boss.y, boss.color, 2.0);
  
  // Handle phase-specific changes
  const newPhase = boss.phaseData[boss.phase].behavior;
  
  switch (newPhase) {
    case 'aggressive':
      // Speed up shield bot orbits
      bossShieldBotsRef.current.forEach(bot => {
        bot.orbitSpeed *= 1.5;
      });
      break;
    case 'disruptive':
      // Next phase logic
      break;
    case 'desperate':
      // Command all shield bots to attack
      commandAllShieldBotsToAttack();
      break;
  }
};

// Command all shield bots to attack
const commandAllShieldBotsToAttack = () => {
  bossShieldBotsRef.current.forEach(bot => {
    bot.state = 'attack';
  });
};

// Hack player controls temporarily
const hackPlayerControls = (duration) => {

  
  // Set a flag to reverse controls
  controlsHackedRef.current = true;
  
  // Don't freeze threats during control hack - makes it more challenging
  // setThreatsFrozen(true); - remove this line
  
  // Create visual effect
  codeLinesRef.current.push({
    id: Date.now() + Math.random(),
    x: playerPosRef.current.x,
    y: playerPosRef.current.y,
    targetX: playerPosRef.current.x,
    targetY: playerPosRef.current.y - 10,
    text: 'CONTROLS HACKED',
    isSpecial: true,
    ttl: duration / 50 // Convert ms to game ticks
  });
  
  // Reset after duration with more reliable setTimeout
  setTimeout(() => {

    controlsHackedRef.current = false;
    // setThreatsFrozen(false); - remove this line
  }, duration);
};

// Process boss each game tick
const processBoss = (deltaTime) => {
  if (!bossRef.current) return;

  const boss = bossRef.current;
  // Get boss from threats array to ensure latest data
  const bossIndex = threatsRef.current.findIndex(t => t.id === boss.id);
  if (bossIndex === -1) {
    // Boss was destroyed
    bossFightComplete();
    return;
  }

  const currentBoss = threatsRef.current[bossIndex];
  bossRef.current = currentBoss; // Keep local ref updated

  // Update current phase based on health
  const healthPercent = currentBoss.health / currentBoss.maxHealth;
  const phaseIndex = currentBoss.phaseData.findIndex(phase => 
    phase.healthThreshold >= healthPercent && 
    (currentBoss.phase < currentBoss.phaseData.indexOf(phase))
  );
  
  // Check for phase transition
  if (phaseIndex !== -1 && phaseIndex > currentBoss.phase) {
    currentBoss.phase = phaseIndex;
    handlePhaseTransition(currentBoss);
  }
  
  // Update behavior based on current phase
  const currentPhase = currentBoss.phaseData[currentBoss.phase].behavior;
  
  // Fix: Make sure the boss actually has lastAttackTime property initialized
  if (currentBoss.lastAttackTime === undefined) {
    currentBoss.lastAttackTime = Date.now();
  }

  switch (currentPhase) {
    case 'shielded':
      // Phase 1: Slow movement, shielded
      updateBossPosition(currentBoss, deltaTime, 0.2); // Slower than before
      break;
    case 'aggressive':
      // Phase 2: Faster movement, more aggressive
      updateBossPosition(currentBoss, deltaTime, 0.6); // Faster than before
      
      // Occasionally spawn additional threats
      if (Date.now() - currentBoss.lastAttackTime > 6000) {
        currentBoss.lastAttackTime = Date.now();
        
        // Add a random threat near the boss
        const angle = Math.random() * Math.PI * 2;
        const distance = 15 + Math.random() * 10;
        createThreat(
          currentBoss.x + Math.cos(angle) * distance,
          currentBoss.y + Math.sin(angle) * distance
        );
      }
      break;
    case 'disruptive':
      // Phase 3: Control disruption - Fix: Make sure the hacking timing works
      updateBossPosition(currentBoss, deltaTime, 0.4);
         
      if (Date.now() - currentBoss.lastAttackTime > 8000) {
        console.log("Boss is attempting to hack controls now!");
        currentBoss.lastAttackTime = Date.now();
        
        // Fix: Call the hackPlayerControls function directly
        hackPlayerControls(5000); // Increased duration to 5 seconds for more noticeable effect
        
        // Visual feedback for control hack
        createExplosionParticles(currentBoss.x, currentBoss.y, '#ff00ff', 1.5);
        
        // Add a floating notice about controls being hacked
        codeLinesRef.current.push({
          id: Date.now() + Math.random(),
          x: playerPosRef.current.x,
          y: playerPosRef.current.y,
          targetX: playerPosRef.current.x,
          targetY: playerPosRef.current.y - 15,
          text: "CONTROLS HACKED!",
          isSpecial: true,
          ttl: 100
        });
      }
      break;
    case 'desperate':
      // Phase 4: All-out attack
      updateBossPosition(currentBoss, deltaTime, 0.8); // Much faster
      if (Date.now() - currentBoss.lastAttackTime > 6000) {
        currentBoss.lastAttackTime = Date.now();
        
        commandAllShieldBotsToAttack();
        
        // Also hack controls more frequently in desperate phase
        hackPlayerControls(3000);
        
        // More visual feedback
        createExplosionParticles(currentBoss.x, currentBoss.y, '#ff0000', 1.5);
        
        // Screen shake for dramatic effect
        setScreenShake({
          x: (Math.random() - 0.5) * 8,
          y: (Math.random() - 0.5) * 8
        });
      }
      break;
  }
  
  // Update shield bots
  updateShieldBots(currentBoss, deltaTime);
};

// Handle boss defeat
const bossFightComplete = () => {
  bossFightCompleteRef.current = true;
  bossActiveRef.current = false;
  
  // Award bonus points
  setScore(s => s + 100);
  
  // Restore normal game environment
  setGameAreaClass("");
  setGridStyle({
    backgroundSize: '40px 40px',
    backgroundImage: 'linear-gradient(to right, rgba(60, 100, 177, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(60, 100, 177, 0.1) 1px, transparent 1px)',
    backgroundPosition: 'center center'
  });
  
  // Create victory explosion
  createExplosionParticles(bossRef.current.x, bossRef.current.y, bossRef.current.color, 3.0);
  
   playExplosionSound(4.0); // Higher intensity for boss defeat
  
  // Drop special power-up
  const victoryPowerUp = {
    id: 'victory-' + Date.now(),
    type: 'Packet Analyzer',
    effect: 'reveal',
    value: 15000, // 15 seconds
    duration: 15000,
    color: '#9333ea',
    bgColor: 'bg-purple-900/50',
    icon: <Target size={16} color="#9333ea" />,
    x: bossRef.current.x,
    y: bossRef.current.y,
    createdAt: Date.now(),
    expiresAt: Date.now() + 10000
  };
  
  availablePowerUpsRef.current.push(victoryPowerUp);
  
  // Remove any remaining shield bots
  threatsRef.current = threatsRef.current.filter(t => !t.isShieldBot);
  
  // Show victory message
  codeLinesRef.current.push({
    id: Date.now() + Math.random(),
    x: 50,
    y: 40,
    targetX: 50,
    targetY: 40,
    text: 'BOTNET MASTER DEFEATED',
    isScore: true,
    ttl: 100
  });
};

// Process explosion particles
const processExplosionParticles = () => {
  const now = Date.now();
  const updatedParticles = [];
  
  explosionParticlesRef.current.forEach(particle => {
    const age = now - particle.createdAt;
    
    // Remove expired particles
    if (age >= particle.life) {
      return;
    }
    
    const lifeRatio = age / particle.life;
    
    if (particle.isRing) {
      // Update expanding ring
      particle.size = particle.maxSize * lifeRatio;
      particle.opacity = 0.8 * (1 - lifeRatio);
    } else {
      // Update particles
      particle.x += particle.velX;
      particle.y += particle.velY;
      particle.opacity = 1 - lifeRatio;
      particle.rotation += 2;
    }
    
    updatedParticles.push(particle);
  });
  
  explosionParticlesRef.current = updatedParticles;
};
  
  // Handle manual firing
 const handleManualFire = () => {
  if (isPaused) return;
  const now = Date.now();
  
  // Check if we're still in cooldown period
  if (now - manualFireTimeRef.current < MANUAL_FIRE_COOLDOWN) {
    return; // Still in cooldown, ignore click
  }
  
  if (!gameActive || health <= 0) return;
  
  // Find nearest threat to player
  const playerPos = playerPosRef.current;
  
  
  
  
  const nearestThreat = findNearestThreat(playerPos.x, playerPos.y);
  if (!nearestThreat) {
    return;
  }
  
  // Get multiShot count from player stats
  const shotsToFire = playerStatsRef.current.multiShot || 1;
  
  for (let i = 0; i < shotsToFire; i++) {
    // Add slight angle variation for multiple shots
    let targetX = nearestThreat.x;
    let targetY = nearestThreat.y;
    
    if (shotsToFire > 1 && i > 0) {
      // Add spread for multiple shots
      const spreadAngle = (i / (shotsToFire - 1) - 0.5) * 0.3; // Spread range in radians
      const distance = Math.sqrt(
        Math.pow(targetX - playerPos.x, 2) + 
        Math.pow(targetY - playerPos.y, 2)
      );
      const baseAngle = Math.atan2(targetY - playerPos.y, targetX - playerPos.x);
      const newAngle = baseAngle + spreadAngle;
      
      targetX = playerPos.x + Math.cos(newAngle) * distance;
      targetY = playerPos.y + Math.sin(newAngle) * distance;
    }
    
// Create a new code line
lastCodeLineIdRef.current++;
const newCodeLine = {
  id: lastCodeLineIdRef.current,
  x: playerPos.x,
  y: playerPos.y,
  targetX: targetX,
  targetY: targetY,
  targetId: nearestThreat.id,
  text: CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)],
  damage: calculateBulletDamage(),
  piercingLeft: playerStatsRef.current.bulletPiercing || 0,
  source: 'manual',
  size: playerStatsRef.current.bulletSize || 1
};
        codeLinesRef.current.push(newCodeLine);
  }
  
  // Update the manual fire time
  manualFireTimeRef.current = now;
};

const calculateBulletDamage = () => {
  const baseDamage = playerStatsRef.current.damage;
  const damageMultiplier = playerStatsRef.current.damageMultiplier || 1;
  
  // Check for critical hit
  const critChance = playerStatsRef.current.critChance || 0;
  const isCritical = Math.random() < critChance;
  
  let finalDamage = baseDamage * damageMultiplier;
  
  if (isCritical) {
    finalDamage *= (playerStatsRef.current.critDamage || 2);
    
    // Create visual feedback for critical hit
    codeLinesRef.current.push({
      id: Date.now() + Math.random(),
      x: playerPosRef.current.x,
      y: playerPosRef.current.y,
      targetX: playerPosRef.current.x + (Math.random() * 10 - 5),
      targetY: playerPosRef.current.y - 10,
      text: 'CRITICAL',
      isSpecial: true,
      ttl: 20
    });
  }
  
  return Math.floor(finalDamage);
};

  // Special ability activation (E key)
  const activateSpecialAbility = () => {
      if (isPaused || !gameActive || !selectedClass || health <= 0 || playerDeadRef.current) return;
    
    // Check cooldown
    const now = Date.now();
	 const cooldownReduction = playerStatsRef.current.cooldownReduction || 0;
  const actualCooldown = PLAYER_CLASSES[selectedClass].cooldown * (1 - cooldownReduction);
  
  if (now - specialCooldownTimeRef.current < actualCooldown) {

    return;
  }

    if (now - specialCooldownTimeRef.current < PLAYER_CLASSES[selectedClass].cooldown) {
  
      return;
    }
    
   
    
    // Start cooldown
    specialCooldownTimeRef.current = now;
    setSpecialCooldown(PLAYER_CLASSES[selectedClass].cooldown);
    
    // Add visual feedback for special ability
    activePowerUpsRef.current.push({
      id: Date.now(),
      name: PLAYER_CLASSES[selectedClass].specialAbility,
      duration: 3000,
      startTime: Date.now(),
      expiresAt: Date.now() + 3000,
      color: PLAYER_CLASSES[selectedClass].color,
      icon: PLAYER_CLASSES[selectedClass].icon
    });
    
    // Class-specific ability implementation
    switch (selectedClass) {
      case 'FIREWALL':
        // Create a protective barrier that damages nearby threats
        const playerPos = playerPosRef.current;
        
        // Check all threats within range
        threatsRef.current.forEach(threat => {
          const dx = playerPos.x - threat.x;
          const dy = playerPos.y - threat.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Damage all threats within the barrier radius
          if (distance < 25) {
            threat.health -= 30;
            
            // Create visual effect for damage
            codeLinesRef.current.push({
              id: Date.now() + Math.random(),
              x: playerPos.x,
              y: playerPos.y,
              targetX: threat.x,
              targetY: threat.y,
              text: 'firewall.protect()',
              damage: 30,
              isSpecial: true
            });
          }
        });
        break;
        
      case 'AZURE':
        // Cloud burst attack - hits multiple threats
        const targets = [...threatsRef.current].slice(0, 5);
        const pos = playerPosRef.current;
        
        targets.forEach(threat => {
          codeLinesRef.current.push({
            id: Date.now() + Math.random(),
            x: pos.x,
            y: pos.y,
            targetX: threat.x,
            targetY: threat.y,
            text: 'cloud.deploy()',
            damage: 40,
            isSpecial: true
          });
        });
        break;
        
      case 'NETWORK':
        // Reveal and slow all threats
        threatsRef.current.forEach(threat => {
          threat.visible = true; // Reveal stealth threats
          threat.slowed = true;
          threat.slowedUntil = Date.now() + 7000;
        });
        break;
        
      case 'PENTESTER':
        // Critical hit on nearest threat
        const nearest = findNearestThreat(playerPosRef.current.x, playerPosRef.current.y);
        if (nearest) {
          const found = threatsRef.current.find(t => t.id === nearest.id);
          if (found) {
            found.health -= 100;
            
            codeLinesRef.current.push({
              id: Date.now() + Math.random(),
              x: playerPosRef.current.x,
              y: playerPosRef.current.y,
              targetX: found.x,
              targetY: found.y,
              text: 'exploit.execute()',
              damage: 100,
              isSpecial: true
            });
          }
        }
        break;
        
      default:
    
    }
    
// Force update to show special ability effects
// Fix 4: Update the setTick function to check boss triggers less frequently
setTick(t => {
  const newTick = t + 1;
  // Only check boss trigger conditions every 30 ticks (1.5 seconds at 50ms tick rate)
  // This is frequent enough for game responsiveness but cuts down on console spam
  if (newTick % 30 === 0 && score > 450 && score < 550) {
    checkBossTrigger();
  }
  return newTick;
});
  };
  
  // Toggle auto-fire
  const toggleAutoFire = () => {
    setAutoFire(!autoFire);
  };
  
   // Handle mouse movement
const handleMouseMove = (e) => {
  if (!gameAreaRef.current || !gameActive || isPaused) return;
    
  const rect = gameAreaRef.current.getBoundingClientRect();
  let x = ((e.clientX - rect.left) / rect.width) * 100;
  let y = ((e.clientY - rect.top) / rect.height) * 100;
  
  // Apply control hack if active - make sure this logic works
  if (controlsHackedRef.current) {
      // More extreme inversion for more noticeable effect
    x = 100 - x;
    y = 100 - y;
  }

  // Add the current position to history
  playerPositionHistoryRef.current.push({...playerPosRef.current});
  // Keep only last 5 positions
  if (playerPositionHistoryRef.current.length > 5) {
    playerPositionHistoryRef.current.shift();
  }
  
  playerPosRef.current = { x, y };
};
  
  // Process code lines movement and collisions
 // Process code lines movement and collisions
const processCodeLines = () => {
  const isPlayerDead = health <= 0;
  const newCodeLines = [];

  codeLinesRef.current.forEach(line => {
    if (isPlayerDead && line.damage > 0) {
      newCodeLines.push(line);
      return;
    }
    
    // If it has a TTL, decrement it
    if (line.ttl !== undefined) {
      line.ttl--;
      if (line.ttl <= 0) {
        return; // Skip this line, it's expired
      }
    }
    
    const dx = line.targetX - line.x;
    const dy = line.targetY - line.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
        
    // Check for hit
    if (distance < 3) {
      // Find the target threat by ID
      let targetThreatIndex = -1;
      let targetThreat = null;
      
      for (let i = 0; i < threatsRef.current.length; i++) {
        if (threatsRef.current[i].id === line.targetId) {
          targetThreatIndex = i;
          targetThreat = threatsRef.current[i];
          break;
        }
      }
      
      if (targetThreat) {
        // Apply damage
        const resistanceFactor = 1 - (targetThreat.resistance || 0);
        const actualDamage = Math.floor(line.damage * resistanceFactor);
        targetThreat.health -= actualDamage;
        
        if (targetThreat.resistance > 0) {
          // Create a small visual effect to show resistance
          codeLinesRef.current.push({
            id: Date.now() + Math.random(),
            x: targetThreat.x,
            y: targetThreat.y,
            targetX: targetThreat.x + (Math.random() * 10 - 5),
            targetY: targetThreat.y + (Math.random() * 10 - 5),
            text: 'RESIST',
            damage: 0,
            isResist: true,
            ttl: 20 // Short time to live
          });
        }
            
        // Check if threat is destroyed
        if (targetThreat.health <= 0) {
          handleThreatDestroyed(targetThreat);
        }
        
        // Check for bullet splitting (Recursive Function upgrade)
        const splitChance = playerStatsRef.current.splitChance || 0;
        if (Math.random() < splitChance) {
          // Find nearby threats to target with split bullets
          const nearbyThreats = [];
          threatsRef.current.forEach(threat => {
            if (threat.id !== targetThreat.id && threat.visible) {
              const tdx = threat.x - targetThreat.x;
              const tdy = threat.y - targetThreat.y;
              const tdist = Math.sqrt(tdx * tdx + tdy * tdy);
              
              // Only consider threats within a reasonable distance
              if (tdist < 30) {
                nearbyThreats.push({
                  ...threat,
                  distance: tdist
                });
              }
            }
          });
          
          // Sort by distance and take up to 2 closest threats
          nearbyThreats.sort((a, b) => a.distance - b.distance);
          const targetsToSplit = nearbyThreats.slice(0, 2);
          
          // Only create split bullets if we have targets
          if (targetsToSplit.length > 0) {
            // Create visual effect for splitting
            createExplosionParticles(
              targetThreat.x, 
              targetThreat.y, 
              '#f87171', // Red color for split
              0.5
            );
            
            // Create split bullets
            targetsToSplit.forEach(splitTarget => {
              codeLinesRef.current.push({
                id: Date.now() + Math.random(),
                x: targetThreat.x,
                y: targetThreat.y,
                targetX: splitTarget.x,
                targetY: splitTarget.y,
                targetId: splitTarget.id,
                text: 'split()',
                damage: Math.floor(line.damage * 0.6), // Split bullets do 60% damage
                piercingLeft: 0, // Split bullets don't pierce
                source: 'split',
                isSplit: true,
                ttl: 30 // In case the target is destroyed before hit
              });
            });
          }
        }
        
        // Check if the bullet should continue (piercing upgrade)
        if ((line.piercingLeft !== undefined) && line.piercingLeft > 0) {
          line.piercingLeft--;
          
          // Find another target in the same direction
          const nextTarget = findNextTargetInDirection(
            line.x, line.y, 
            dx, dy
          );
          
          if (nextTarget) {
            // Update the trajectory to the new target
            line.targetId = nextTarget.id;
            line.targetX = nextTarget.x;
            line.targetY = nextTarget.y;
            
            // Create visual trail effect for piercing bullet
            codeLinesRef.current.push({
              id: Date.now() + Math.random(),
              x: targetThreat.x,
              y: targetThreat.y,
              targetX: targetThreat.x + (Math.random() * 8 - 4),
              targetY: targetThreat.y + (Math.random() * 8 - 4),
              text: '>>',
              isSpecial: true,
              ttl: 10
            });
            
            newCodeLines.push(line);
          }
        }
      } else {
        // Fallback: check threats near the target position
        let hitIndex = -1;
        
        for (let i = 0; i < threatsRef.current.length; i++) {
          const threat = threatsRef.current[i];
          const dx = threat.x - line.targetX;
          const dy = threat.y - line.targetY;
          const threatDist = Math.sqrt(dx * dx + dy * dy);
          
          if (threatDist < 8) {
            hitIndex = i;
            break;
          }
        }
        
        if (hitIndex >= 0) {
          const hitThreat = threatsRef.current[hitIndex];
          
          // Apply damage
          const resistanceFactor = 1 - (hitThreat.resistance || 0);
          const actualDamage = Math.floor(line.damage * resistanceFactor);
          hitThreat.health -= actualDamage;
          
          if (hitThreat.resistance > 0) {
            // Create a small visual effect to show resistance
            codeLinesRef.current.push({
              id: Date.now() + Math.random(),
              x: hitThreat.x,
              y: hitThreat.y,
              targetX: hitThreat.x + (Math.random() * 10 - 5),
              targetY: hitThreat.y + (Math.random() * 10 - 5),
              text: 'RESIST',
              damage: 0,
              isResist: true,
              ttl: 20 // Short time to live
            });
          }
          
          // Check if threat is destroyed
          if (hitThreat.health <= 0) {
            handleThreatDestroyed(hitThreat);
          }
        }
      }
    } else {
      if (!line.ttl && distance > 0 && Math.abs(dx) + Math.abs(dy) > 150) {
        line.ttl = 30; // Will disappear after 30 ticks
      }
      
      // Move the code line
      const speed = line.isSpecial ? 3 : 2;
      if (distance > 0) {
        line.x += (dx / distance) * speed;
        line.y += (dy / distance) * speed;
      }
      newCodeLines.push(line);
    }
  });
  
  codeLinesRef.current = newCodeLines;
};

// Helper function to find the next target in the bullet's direction
const findNextTargetInDirection = (x, y, dirX, dirY) => {
  // Normalize direction vector
  const length = Math.sqrt(dirX * dirX + dirY * dirY);
  const normDirX = dirX / length;
  const normDirY = dirY / length;
  
  // Get the current class's range
  const playerRange = selectedClass ? 
    PLAYER_CLASSES[selectedClass].range * (playerStatsRef.current.rangeMultiplier || 1) : 
    CODE_BULLET_RANGE;
  
  const potentialTargets = [];
  
  // Loop through threats to find ones that are in our direction
  threatsRef.current.forEach(threat => {
    // Only consider visible threats
    if (!threat.visible) return;
    
    // Calculate vector from start to threat
    const threatDx = threat.x - x;
    const threatDy = threat.y - y;
    const threatDist = Math.sqrt(threatDx * threatDx + threatDy * threatDy);
    
    // Check if threat is within range
    if (threatDist > playerRange || threatDist < 5) return; // Skip if too far or too close
    
    // Calculate dot product to check if threat is in our direction
    const dotProduct = (normDirX * threatDx + normDirY * threatDy) / threatDist;
    
    // Threat is in our direction if the dot product is positive (less than 90 degree angle)
    if (dotProduct > 0.7) { // Only consider threats within ~45 degree cone
      potentialTargets.push({
        ...threat,
        distance: threatDist, 
        alignment: dotProduct // How well aligned with our direction
      });
    }
  });
  
  // Sort by distance and alignment to get the most suitable next target
  potentialTargets.sort((a, b) => {
    // Prioritize alignment first, then distance
    const alignmentDiff = b.alignment - a.alignment;
    if (Math.abs(alignmentDiff) > 0.2) return alignmentDiff;
    return a.distance - b.distance;
  });
  
  return potentialTargets.length > 0 ? potentialTargets[0] : null;
};
  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
		
		if (e.key === 'p' || e.key === 'P') {
      togglePause();
    }
		
      if (e.key === 'e' || e.key === 'E') {
        isSpecialKeyPressedRef.current = true;
        activateSpecialAbility();
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'e' || e.key === 'E') {
        isSpecialKeyPressedRef.current = false;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedClass, gameActive, health]);
  
  // Game loop
// This is the fixed section of code for the game loop in CyberGame.jsx
// First, let's fix the structure of the gameLoop function and the enclosing useEffect

// Game loop
useEffect(() => {
  if (!gameActive) return;
 
  let lastTime = Date.now();
  let threatSpawnTime = 0;
  
  const gameLoop = () => {
     if (effectivePaused) {
    // Still force a re-render to ensure the UI updates
    setTick(t => t + 1);
    return;
  }
    const now = Date.now();
    const deltaTime = now - lastTime;
    lastTime = now;
    
    // Process code lines
    processCodeLines();

    // Process explosion particles
    processExplosionParticles();

    // Remove threats that are out of bounds (safety cleanup)
    threatsRef.current = threatsRef.current.filter(threat => {
      return threat.x >= -10 && threat.x <= 110 && threat.y >= -10 && threat.y <= 110;
    });
	

    // Check for boss triggers and update boss
  // Only check boss state if it's active, no need for trigger checks every frame
if (bossActiveRef.current) {
  processBoss(deltaTime);
}
        
    // Update special ability cooldown
    if (specialCooldownTimeRef.current > 0) {
      // Apply cooldown reduction from upgrades
      const cooldownReduction = playerStatsRef.current.cooldownReduction || 0;
      const actualCooldown = PLAYER_CLASSES[selectedClass].cooldown * (1 - cooldownReduction);
      
      const cooldownRemaining = Math.max(0, 
        actualCooldown - (now - specialCooldownTimeRef.current)
      );
      setSpecialCooldown(cooldownRemaining);
    }
        
    // Spawn threats based on dynamic spawn rate
    threatSpawnTime += deltaTime;
    if (threatSpawnTime >= threatSpawnRateRef.current) {
      createThreat();
      threatSpawnTime = 0;
    }
        
// Check level up condition (every 15 threats)
if (threatsDestroyed > 0 && threatsDestroyed % 15 === 0 && threatsDestroyed / 15 + 1 > level) {
  const newLevel = Math.floor(threatsDestroyed / 15) + 1;
  setLevel(newLevel);
  
  // Add health recovery on level up (10% of max health)
  if (selectedClass && health > 0 && !playerDeadRef.current) {
    const maxHealth = PLAYER_CLASSES[selectedClass].stats.health + 
                     (playerStatsRef.current.maxHealthBonus || 0);
    const recoveryAmount = Math.ceil(maxHealth * 0.1); // 10% recovery
    
    setHealthWithDeathCheck(h => Math.min(h + recoveryAmount, maxHealth));
    
    // Visual feedback for health recovery
    codeLinesRef.current.push({
      id: Date.now() + Math.random(),
      x: playerPosRef.current.x,
      y: playerPosRef.current.y,
      targetX: playerPosRef.current.x,
      targetY: playerPosRef.current.y - 10,
      text: `LEVEL UP: +${recoveryAmount} HP`,
      isSpecial: true,
      ttl: 60
    });
  }
  
  // Increase game difficulty
  threatSpawnRateRef.current = Math.max(
    600, // Increased minimum spawn time from 400
    threatSpawnRateRef.current * 0.85 // Reduce spawn time by 15% on level up
  );
}
    // Update active power-ups and handle expirations
    const expiredPowerUps = [];
    activePowerUpsRef.current = activePowerUpsRef.current.filter(powerUp => {
      const isExpired = now > powerUp.expiresAt;
      if (isExpired) {
        expiredPowerUps.push(powerUp);
      }
      return !isExpired;
    });
        
    // Handle expired power-ups
    expiredPowerUps.forEach(powerUp => {
      if (powerUp.effect === 'damage') {
        playerStatsRef.current.damage = PLAYER_CLASSES[selectedClass].stats.damage;
      } else if (powerUp.effect === 'fireRate') {
        playerStatsRef.current.fireRate = PLAYER_CLASSES[selectedClass].stats.fireRate;
      } else if (powerUp.effect === 'shield') {
        shieldChargesRef.current = 0;
      }
    });
        
    // Clean up expired available power-ups
    availablePowerUpsRef.current = availablePowerUpsRef.current.filter(
      powerUp => now < powerUp.expiresAt
    );
        
    // Auto-fire logic
    if (autoFire && selectedClass && health > 0) {
      const fireRate = playerStatsRef.current.fireRate;
          
      if (now - autoFireTimeRef.current >= fireRate) {
        // Find nearest threat to player
        const playerPos = playerPosRef.current;
        const nearestThreat = findNearestThreat(playerPos.x, playerPos.y);
            
        if (nearestThreat) {
          // Get multiShot count from player stats
          const shotsToFire = playerStatsRef.current.multiShot || 1;
          
          for (let i = 0; i < shotsToFire; i++) {
            // Add slight angle variation for multiple shots
            let targetX = nearestThreat.x;
            let targetY = nearestThreat.y;
            
            if (shotsToFire > 1 && i > 0) {
              // Add spread for multiple shots
              const spreadAngle = (i / (shotsToFire - 1) - 0.5) * 0.3; // Spread range in radians
              const distance = nearestThreat.distance;
              const baseAngle = Math.atan2(targetY - playerPos.y, targetX - playerPos.x);
              const newAngle = baseAngle + spreadAngle;
              
              targetX = playerPos.x + Math.cos(newAngle) * distance;
              targetY = playerPos.y + Math.sin(newAngle) * distance;
            }
            
            // Create a new code line
            lastCodeLineIdRef.current++;
            const newCodeLine = {
              id: lastCodeLineIdRef.current,
              x: playerPos.x,
              y: playerPos.y,
              targetX: targetX,
              targetY: targetY,
              targetId: nearestThreat.id,
              text: CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)],
              damage: calculateBulletDamage(),
              piercingLeft: playerStatsRef.current.bulletPiercing || 0,
              source: 'auto',
              size: playerStatsRef.current.bulletSize || 1
            };
              
            codeLinesRef.current.push(newCodeLine);
          }
          
          autoFireTimeRef.current = now;
        }
      }
    }
    
    // Health regeneration (Backup System upgrade)
    if (playerStatsRef.current.healthRegen > 0 && health > 0 && !playerDeadRef.current) {
      if (!playerStatsRef.current.lastRegenTime) {
        playerStatsRef.current.lastRegenTime = now;
      }
      
      const regenInterval = playerStatsRef.current.healthRegenInterval;
      
      if (now - playerStatsRef.current.lastRegenTime >= regenInterval) {
        // Calculate health to regenerate
        const maxHealth = PLAYER_CLASSES[selectedClass].stats.health + 
                        (playerStatsRef.current.maxHealthBonus || 0);
        const regenAmount = Math.ceil(maxHealth * playerStatsRef.current.healthRegen);
        
        // Apply regeneration
        if (health < maxHealth) {
          setHealthWithDeathCheck(h => Math.min(h + regenAmount, maxHealth));
          
          // Show regen effect (small visual feedback)
          if (Math.random() < 0.3) { // Only show occasionally to avoid spam
            codeLinesRef.current.push({
              id: Date.now() + Math.random(),
              x: playerPosRef.current.x,
              y: playerPosRef.current.y,
              targetX: playerPosRef.current.x,
              targetY: playerPosRef.current.y - 10,
              text: '+' + regenAmount,
              isSpecial: true,
              ttl: 20
            });
          }
        }
        
        playerStatsRef.current.lastRegenTime = now;
      }
    }
    
    // Move threats based on their behavior
const playerPos = playerPosRef.current;
threatsRef.current.forEach(threat => {
  // Apply threat scanner upgrade
  if (playerStatsRef.current.revealStealth && !threat.visible) {
    threat.visible = true;
  }
  
  // ... rest of existing threat logic continues below
      if (health <= 0 || threatsFrozen) {
      }
      
      const dx = playerPos.x - threat.x;
      const dy = playerPos.y - threat.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
          
      // Update stealth threats (reveal when close)
      if (threat.behavior === 'stealth' && !threat.visible && distance < 15) {
        threat.visible = true;
      }
          
      // Handle movement based on behavior type
      threat.moveTime += deltaTime;
          
      if (distance > 0) {
        // Apply speed multiplier from upgrades
        const baseSpeedMultiplier = selectedClass ? PLAYER_CLASSES[selectedClass].stats.speed : 1;
        const upgradeSpeedMultiplier = playerStatsRef.current.speedMultiplier || 1;
        let speed = 0.5 / (baseSpeedMultiplier * upgradeSpeedMultiplier);
            
        // Apply slowing effect if active
        if (threat.slowed && threat.slowedUntil > now) {
          speed *= 0.5;
        } else {
          threat.slowed = false;
        }
            
        let moveStandard = true;
            
        // Behavior-specific movement
        switch(threat.behavior) {
          case 'fast':
            speed *= 1.6; // Fast threats move 60% faster
            break;
                
          case 'erratic':
            // Change direction randomly
            if (threat.moveTime > 300) {
              threat.moveDirection = Math.random() * 2 * Math.PI;
              threat.moveTime = 0;
            }
                
            // 70% of the time move erratically, 30% toward player
            if (Math.random() < 0.8) {
              threat.x += Math.cos(threat.moveDirection) * speed;
              threat.y += Math.sin(threat.moveDirection) * speed;
              moveStandard = false; // Don't do standard movement
            }
            break;
                
          case 'stealth':
            // Stealth threats move slower when invisible, faster when visible
            speed *= threat.visible ? 1.5 : 0.7;
            break;
                
          case 'explosive':
            // These threats get faster as they get closer
            speed *= (1 + (1 - Math.min(1, distance / 50)) * 2.0);
            break;
        }
            
        // Standard movement toward player
        if (moveStandard) {
          threat.x += (dx / distance) * speed;
          threat.y += (dy / distance) * speed;
        }
            
        // Check for collision with player
        if (distance < 5 && health > 0) {
          // Add this check to prevent boss destruction
          if (threat.isBoss) {
            // Only damage player, don't destroy boss
            if (shieldChargesRef.current > 0) {
              shieldChargesRef.current--;
                  
              // Update shield power-up display
              const shieldPowerUp = activePowerUpsRef.current.find(p => p.effect === 'shield');
              if (shieldPowerUp) {
                shieldPowerUp.charges = shieldChargesRef.current;
              }
                  
              // Remove the shield completely if no charges left
              if (shieldChargesRef.current === 0) {
                activePowerUpsRef.current = activePowerUpsRef.current.filter(p => p.effect !== 'shield');
              }
            } else {
              // Apply damage reduction from upgrades
              const damageReduction = playerStatsRef.current.damageReduction || 0;
              
              // Check for dodge chance (Encryption Layer upgrade)
              const dodgeChance = playerStatsRef.current.dodgeChance || 0;
              const dodged = Math.random() < dodgeChance;
              
              if (dodged) {
                // Create dodge text effect
                codeLinesRef.current.push({
                  id: Date.now() + Math.random(),
                  x: playerPosRef.current.x,
                  y: playerPosRef.current.y,
                  targetX: playerPosRef.current.x,
                  targetY: playerPosRef.current.y - 10,
                  text: "DODGED",
                  damage: 0,
                  isSpecial: true,
                  ttl: 30
                });
              } else {
                // Calculate reduced damage (boss damage is already reduced by 50%)
                const reducedDamage = Math.floor(
                  Math.floor(threat.damage * 0.5) * (1 - damageReduction)
                );
                
                // Take damage but at a reduced rate for boss collisions
                setHealthWithDeathCheck(h => Math.max(0, h - reducedDamage));
              }
            }
            
            // Push player back a bit
            const pushback = 2;
            const angle = Math.atan2(playerPos.y - threat.y, playerPos.x - threat.x);
            playerPosRef.current = {
              x: playerPos.x + Math.cos(angle) * pushback,
              y: playerPos.y + Math.sin(angle) * pushback
            };
          } else {
            // Regular threat collision handling
            // Check if shield is active
            if (shieldChargesRef.current > 0) {
              shieldChargesRef.current--;
                  
              // Update shield power-up display
              const shieldPowerUp = activePowerUpsRef.current.find(p => p.effect === 'shield');
              if (shieldPowerUp) {
                shieldPowerUp.charges = shieldChargesRef.current;
              }
                  
              // Remove the shield completely if no charges left
              if (shieldChargesRef.current === 0) {
                activePowerUpsRef.current = activePowerUpsRef.current.filter(p => p.effect !== 'shield');
              }
            } else {
              // Apply damage reduction from upgrades
              const damageReduction = playerStatsRef.current.damageReduction || 0;
              
              // Check for dodge chance (Encryption Layer upgrade)
              const dodgeChance = playerStatsRef.current.dodgeChance || 0;
              const dodged = Math.random() < dodgeChance;
              
              if (dodged) {
                // Create dodge text effect
                codeLinesRef.current.push({
                  id: Date.now() + Math.random(),
                  x: playerPosRef.current.x,
                  y: playerPosRef.current.y,
                  targetX: playerPosRef.current.x,
                  targetY: playerPosRef.current.y - 10,
                  text: "DODGED",
                  damage: 0,
                  isSpecial: true,
                  ttl: 30
                });
              } else {
                // Calculate reduced damage
                const reducedDamage = Math.floor(threat.damage * (1 - damageReduction));
                
                // Take damage based on threat type
                setHealthWithDeathCheck(h => Math.max(0, h - reducedDamage));
              }
            }
                
            // Handle explosive threats
            if (threat.behavior === 'explosive') {
              // Damage nearby threats too
              threatsRef.current.forEach(otherThreat => {
                if (otherThreat.id !== threat.id) {
                  const otherDx = threat.x - otherThreat.x;
                  const otherDy = threat.y - otherThreat.y;
                  const otherDist = Math.sqrt(otherDx * otherDx + otherDy * otherDy);
                      
                  if (otherDist < 15) { // Explosion radius
                    otherThreat.health -= 20;
                  }
                }
              });
            }
                
            // Use handleThreatDestroyed instead of directly removing
            handleThreatDestroyed(threat);
          }
        }
      }
    });
      
  // Check for player collision with power-ups (with loot magnet)
const playerX = playerPosRef.current.x;
const playerY = playerPosRef.current.y;
const lootMagnetRadius = playerStatsRef.current.lootMagnetRadius || 0;

availablePowerUpsRef.current.forEach(powerUp => {
  const dx = playerX - powerUp.x;
  const dy = playerY - powerUp.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Loot magnet effect
  if (lootMagnetRadius > 0 && distance < lootMagnetRadius && distance > 5) {
    // Pull power-up towards player
    const pullStrength = 0.8;
    const pullX = (dx / distance) * pullStrength;
    const pullY = (dy / distance) * pullStrength;
    
    powerUp.x += pullX;
    powerUp.y += pullY;
  }
  
  // Collect power-up when close enough
  if (distance < 5) {
    collectPowerUp(powerUp);
  }
});
	
    // Handle VLAN Keeper line creation
    threatsRef.current.forEach(threat => {
      if (threat.behavior === 'vlan' && threat.visible) {
        // Initialize lastLineCreated if it doesn't exist
        if (threat.lastLineCreated === undefined) {
          threat.lastLineCreated = now - (threat.createLineInterval / 2); // Create first line after half interval
        }
        
        // Check if it's time to create a new barrier line
        if (now - threat.lastLineCreated >= threat.createLineInterval) {
          // Only create a line if we haven't reached the max
          const keeperLines = vlanBarriersRef.current.filter(b => b.vlanKeeperId === threat.id).length;
          if (keeperLines < threat.maxLines) {
            createVLANBarrier(threat);
          }
          threat.lastLineCreated = now;
        }
      }
    });
    
    // Update VLAN barrier animations
    vlanBarriersRef.current.forEach(barrier => {
      // Update pulse phase for visual effect
      barrier.pulsePhase = (barrier.pulsePhase + 0.05) % (2 * Math.PI);
    });
    
    // Check for collisions with VLAN barriers
    checkVLANBarrierCollisions();
    
    // Force re-render once per game tick
    setTick(t => t + 1);
  };

  // Run game loop at fixed interval
  const gameInterval = setInterval(gameLoop, GAME_TICK);
  
  return () => {
    clearInterval(gameInterval);
  };
}, [gameActive, selectedClass, autoFire, level, effectivePaused]);
  
  
  // Open tutorial
  const openTutorial = () => {
    setShowTutorial(true);
  };
  
  // Close tutorial
  const closeTutorial = () => {
    setShowTutorial(false);
  };
  
  // Reset game
  const resetGame = () => {
    setGameActive(false);
    setSelectedClass(null);
    threatsRef.current = [];
	playerDeadRef.current = false; // Reset death state
    codeLinesRef.current = [];
    availablePowerUpsRef.current = [];
    setScore(0);
    setThreatsDestroyed(0);
    setLevel(1);
    setAutoFire(true);
    autoFireTimeRef.current = 0;
    activePowerUpsRef.current = [];
    specialCooldownTimeRef.current = 0;
    setSpecialCooldown(0);
    setHealth(100);
    shieldChargesRef.current = 0;
    threatSpawnRateRef.current = BASE_THREAT_SPAWN_RATE;
	resetBossStates();
	  setUpgrades([]);
  setUpgradeChoices([]);
  setLastUpgradeScore(0);
  setShowUpgradeScreen(false);
  setUpgradeNotification(false);
    if (typingSoundRef.current) {
    typingSoundRef.current.pause();
    typingSoundRef.current = null;
  }
  };
  
  useEffect(() => {
  return () => {
    // Cleanup function when component unmounts
    if (typingSoundRef.current) {
      typingSoundRef.current.pause();
      typingSoundRef.current = null;
    }
  };
}, []);
  
  const healthPercent = selectedClass ? 
    (health / PLAYER_CLASSES[selectedClass].stats.health) * 100 : 100;
    
  const specialCooldownPercent = selectedClass ?
    (specialCooldown / PLAYER_CLASSES[selectedClass].cooldown) * 100 : 0;
	return (
    <div className="w-full h-screen flex flex-col bg-gray-900 text-white overflow-hidden">
      {/* Title bar - much smaller to maximize game space */}
      <h1 className="text-xl font-bold py-1 text-center bg-gray-800 border-b border-gray-700">Shielded Onion Layer Defender</h1>
      
      {/* Tutorial */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="bg-gray-800 p-6 rounded-lg max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-center">How to Play</h2>
            
            <div className="mb-4">
              <h3 className="font-semibold text-blue-400 mb-2">Controls</h3>
              <ul className="list-disc list-inside text-sm">
                <li>Move your mouse to control your character</li>
                <li>Click to fire manually at the nearest threat</li>
                <li>Press <span className="bg-gray-700 px-2 py-1 rounded">E</span> to activate your special ability</li>
                <li>Toggle Auto-Fire to automatically target and attack threats</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-green-400 mb-2">Power-ups</h3>
              <p className="text-sm mb-2">Collect power-ups by moving over them:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {POWER_UP_TYPES.map((powerUp, index) => (
                  <div key={index} className="flex items-center bg-gray-700 p-2 rounded">
                    <div className={`${powerUp.bgColor} p-1 rounded mr-2`}>
                      {powerUp.icon}
                    </div>
                    <div>
                      <p className="font-semibold" style={{color: powerUp.color}}>{powerUp.name}</p>
                      <p className="text-gray-300">{powerUp.effect === 'health' ? 'Restores health' : 
                                                   powerUp.effect === 'damage' ? 'Increases damage' :
                                                   powerUp.effect === 'fireRate' ? 'Increases fire rate' : 
                                                   'Protects from damage'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-red-400 mb-2">Threats</h3>
              <p className="text-sm mb-2">Different threats have unique behaviors:</p>
              <div className="grid grid-cols-1 gap-2 text-xs">
                {THREAT_TYPES.map((threat, index) => (
                  <div key={index} className="flex items-center bg-gray-700 p-2 rounded">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full mr-2" style={{backgroundColor: threat.color}}>
                      {threat.icon}
                    </div>
                    <div>
                      <p className="font-semibold">{threat.name}</p>
                      <p className="text-gray-300">
                        {threat.behavior === 'standard' ? 'Regular movement pattern' :
                         threat.behavior === 'fast' ? 'Moves faster than normal threats' :
                         threat.behavior === 'erratic' ? 'Moves unpredictably' :
                         threat.behavior === 'stealth' ? 'Invisible until close to player' :
                         threat.behavior === 'explosive' ? 'Explodes when destroyed, damaging nearby threats' :
                         threat.behavior === 'splitting' ? 'Splits into smaller bugs when destroyed' :
                         threat.isSplit ? 'Small but fast mini-bugs' : 'Unknown behavior'}
                      </p>
                    </div>
                  </div>
				    
                ))}
              </div>
            </div>
            
			  
      {/* Upgrades section - NEWLY ADDED */}
      <div className="mb-4">
        <h3 className="font-semibold text-yellow-400 mb-2">Upgrades</h3>
        <p className="text-sm mb-2">Earn upgrades every 150 points to enhance your abilities:</p>
        
        <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
          <h4 className="text-red-400 font-semibold">Attack Upgrades</h4>
          {Object.values(UPGRADES)
            .filter(upgrade => upgrade.category === 'attack')
            .map((upgrade, index) => (
              <div key={index} className="bg-gray-700 p-2 rounded flex items-start">
                <div className="bg-red-900/50 p-1 rounded flex-shrink-0 mr-2">
                  {upgrade.icon}
                </div>
                <div>
                  <p className="font-semibold text-red-400 text-xs">{upgrade.name}</p>
                  <p className="text-gray-300 text-xs">{upgrade.description}</p>
                  <p className="text-gray-400 text-xs mt-1">Max Level: {upgrade.maxLevel}</p>
                </div>
              </div>
            ))}
            
          <h4 className="text-green-400 font-semibold mt-3">Defense Upgrades</h4>
          {Object.values(UPGRADES)
            .filter(upgrade => upgrade.category === 'defense')
            .map((upgrade, index) => (
              <div key={index} className="bg-gray-700 p-2 rounded flex items-start">
                <div className="bg-green-900/50 p-1 rounded flex-shrink-0 mr-2">
                  {upgrade.icon}
                </div>
                <div>
                  <p className="font-semibold text-green-400 text-xs">{upgrade.name}</p>
                  <p className="text-gray-300 text-xs">{upgrade.description}</p>
                  <p className="text-gray-400 text-xs mt-1">Max Level: {upgrade.maxLevel}</p>
                </div>
              </div>
            ))}
            
          <h4 className="text-blue-400 font-semibold mt-3">Utility Upgrades</h4>
          {Object.values(UPGRADES)
            .filter(upgrade => upgrade.category === 'utility')
            .map((upgrade, index) => (
              <div key={index} className="bg-gray-700 p-2 rounded flex items-start">
                <div className="bg-blue-900/50 p-1 rounded flex-shrink-0 mr-2">
                  {upgrade.icon}
                </div>
                <div>
                  <p className="font-semibold text-blue-400 text-xs">{upgrade.name}</p>
                  <p className="text-gray-300 text-xs">{upgrade.description}</p>
                  <p className="text-gray-400 text-xs mt-1">Max Level: {upgrade.maxLevel}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
      
			
            <button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded mt-4"
              onClick={closeTutorial}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
	  {gameActive && (
        <div className="relative w-full flex-grow flex flex-col">
          {/* Game area */}
<div 
  ref={gameAreaRef}
  onClick={handleManualFire}
  onMouseMove={handleMouseMove}
  className={`absolute inset-0 bg-gray-800 overflow-hidden ${gameAreaClass}`}
  style={{ 
    cursor: "crosshair",
    backgroundImage: 'radial-gradient(circle at center, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 1) 100%)',
    ...gridStyle,
    boxShadow: 'inset 0 0 50px rgba(0, 0, 0, 0.5)',
    transform: `translate(${screenShake.x}px, ${screenShake.y}px)`,
    transition: screenShake.x === 0 && screenShake.y === 0 ? 'transform 0.2s' : 'none'
  }}
>
{/* Upgrade notification banner */}
{upgradeNotification && !showUpgradeScreen && (
  <div 
    className="absolute top-16 left-0 right-0 mx-auto text-center z-40 animate-pulse"
  >
    <div className="bg-yellow-600 bg-opacity-80 text-white px-6 py-3 rounded-lg inline-block">
      <span className="text-xl font-bold">Upgrade Available!</span>
    </div>
  </div>
)}
       {/* Grid lines for cyber effect */}
<div className="absolute inset-0 z-0" 
  style={{
    backgroundSize: '50px 50px, 50px 50px, 50px 50px, 10px 10px, 10px 10px',
    backgroundImage: 
      "radial-gradient(circle at 25px 25px, rgba(40, 200, 80, 0.2) 2px, transparent 0)," +
      "linear-gradient(to right, rgba(40, 200, 80, 0.1) 1px, transparent 1px)," +
      "linear-gradient(to bottom, rgba(40, 200, 80, 0.1) 1px, transparent 1px)," +
      "linear-gradient(to right, rgba(40, 200, 80, 0.05) 0.5px, transparent 1px)," +
      "linear-gradient(to bottom, rgba(40, 200, 80, 0.05) 0.5px, transparent 1px)",
    backgroundPosition: 'center center'
  }}
></div>
            {/* Range indicator */}
            <div 
              className="absolute rounded-full border-2 border-dashed border-white border-opacity-20 z-5"
              style={{
                left: `${playerPosRef.current.x}%`,
                top: `${playerPosRef.current.y}%`,
                width: `${(selectedClass ? PLAYER_CLASSES[selectedClass].range : CODE_BULLET_RANGE) * 2}%`,
                height: `${(selectedClass ? PLAYER_CLASSES[selectedClass].range : CODE_BULLET_RANGE) * 2}%`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                borderColor: PLAYER_CLASSES[selectedClass]?.color || 'rgba(255, 255, 255, 0.2)'
              }}
            ></div>
            
            {/* Available Power-Ups */}
            {availablePowerUpsRef.current.map(powerUp => (
              <div
                key={powerUp.id}
                className="absolute z-20 w-8 h-8 rounded-full flex items-center justify-center animate-pulse"
                style={{
                  left: `${powerUp.x}%`,
                  top: `${powerUp.y}%`,
                  backgroundColor: powerUp.color + '50',
                  boxShadow: `0 0 10px ${powerUp.color}`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {powerUp.icon}
              </div>
            ))}
            
{/* Player */}
<div
  className={`absolute rounded-full flex items-center justify-center z-30 
    ${shieldChargesRef.current > 0 ? 'animate-pulse' : ''}
    ${healthPercent < 30 ? 'warning-flash' : ''}`}
  style={{
    left: `${playerPosRef.current.x}%`,
    top: `${playerPosRef.current.y}%`,
    transform: 'translate(-50%, -50%)',
    width: '40px',
    height: '40px',
    backgroundColor: PLAYER_CLASSES[selectedClass]?.color || "#3b82f680",
    boxShadow: shieldChargesRef.current > 0 
      ? `0 0 15px #c084fc, 0 0 5px rgba(255, 255, 255, 0.5)` 
      : healthPercent < 30
        ? '0 0 10px rgba(255, 0, 0, 0.7)' 
        : '0 0 10px rgba(255, 255, 255, 0.3)'
  }}
>
  {PLAYER_CLASSES[selectedClass]?.icon}
  {shieldChargesRef.current > 0 && (
    <div className="absolute -top-3 -right-1 bg-purple-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
      {shieldChargesRef.current}
    </div>
  )}
</div>
            
            {/* Threats */}
            {threatsRef.current.map(threat => (
              <div
                key={threat.id}
                className={`absolute rounded flex items-center justify-center text-white text-xs z-10 ${threat.slowed ? 'opacity-70' : ''} ${!threat.visible ? 'opacity-30' : ''}`}
                style={{
                  width: `${30 * (threat.size || 1)}px`, // Apply size multiplier
                  height: `${30 * (threat.size || 1)}px`, // Apply size multiplier
                  backgroundColor: threat.visible ? threat.color : 'transparent',
                  border: threat.visible ? 'none' : `2px dashed ${threat.color}`,
                  left: `${threat.x}%`,
                  top: `${threat.y}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: threat.slowed 
                    ? '0 0 8px rgba(0, 255, 255, 0.5)' 
                    : threat.behavior === 'explosive' 
                      ? '0 0 8px rgba(255, 150, 0, 0.5)'
                      : threat.behavior === 'splitting'
                        ? '0 0 8px rgba(236, 72, 153, 0.5)'
                        : '0 0 5px rgba(0, 0, 0, 0.5)',
                  transition: 'box-shadow 0.3s',
                  fontSize: threat.size > 1 ? '1.2em' : threat.size < 1 ? '0.8em' : '1em' // Adjust icon size
                }}
              >
                {threat.visible && threat.icon}
                {threat.health < threat.maxHealth && threat.visible && (
                  <div 
                    className="absolute -bottom-5 w-full h-1 bg-gray-700 rounded"
                  >
                    <div 
                      className="h-full bg-green-500 rounded"
                      style={{ width: `${(threat.health / threat.maxHealth) * 100}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ))}
            
{/* Code Lines */}
{codeLinesRef.current.map(line => (
  <div
    key={line.id}
    className={`absolute font-mono z-5 ${
      line.isSpecial ? 'text-yellow-400' : 
      line.isResist ? 'text-red-400' : 
      line.isScore ? 'text-yellow-300 font-bold' :
      'text-green-400'
    }`}
    style={{
      left: `${line.x}%`,
      top: `${line.y}%`,
      transform: 'translate(-50%, -50%)',
      textShadow: line.isSpecial ? 
        '0 0 5px rgba(255,255,0,0.8)' : 
        line.isResist ?
        '0 0 5px rgba(255,0,0,0.8)' :
        line.isScore ?
        '0 0 5px rgba(255,215,0,0.8)' :
        '0 0 5px rgba(0,255,0,0.5)',
      zIndex: 5,
      opacity: line.ttl ? line.ttl / 20 : 1, // Fade out based on TTL
      fontSize: line.isScore ? '14px' : 
               line.isSpecial ? '13px' :
               line.size ? `${12 * line.size}px` : '12px' // Apply bullet size to font
    }}
  >
    {line.text}
  </div>
))}

{/* VLAN Barriers */}
{vlanBarriersRef.current.map(barrier => (
  <div
    key={barrier.id}
    className="absolute z-15" 
    style={{
      left: `${barrier.x1}%`,
      top: `${barrier.y1}%`,
  width: barrier.isHorizontal ? '100%' : '4px', // Increased from 2px to 4px
      height: barrier.isHorizontal ? '4px' : '100%', // Increased from 2px to 4px
      backgroundColor: barrier.color,
      boxShadow: `0 0 ${8 + Math.sin(barrier.pulsePhase) * 4}px ${barrier.color}`,
      opacity: 0.7 + Math.sin(barrier.pulsePhase) * 0.3,
      transition: 'box-shadow 0.1s, opacity 0.1s',
      pointerEvents: 'none'
    }}
  />
))}
            
			{/* Explosion Particles */}
{explosionParticlesRef.current.map(particle => (
  particle.isRing ? (
    <div
      key={particle.id}
      className="absolute rounded-full z-10"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        width: `${particle.size}px`,
        height: `${particle.size}px`,
        border: `2px solid ${particle.color}`,
        transform: 'translate(-50%, -50%)',
        opacity: particle.opacity,
        boxShadow: `0 0 10px ${particle.color}`
      }}
    />
  ) : (
    <div
      key={particle.id}
      className="absolute text-xs font-mono z-10"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        color: particle.color,
        textShadow: `0 0 3px ${particle.color}`,
        transform: `translate(-50%, -50%) rotate(${particle.rotation}deg)`,
        opacity: particle.opacity,
        fontSize: `${particle.size}px`
      }}
    >
      {particle.text}
    </div>
  )
))}
			
            {/* Level notification */}
            {level > 1 && (
              <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-purple-900/80 text-white px-4 py-2 rounded z-40 text-sm">
                Level {level}
              </div>
            )}
            
            {/* Current game tick - invisible */}
            <div style={{ display: 'none' }}>{tick}</div>
          </div>
		 
{showUpgradeScreen && (
  <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
    <div className="bg-gray-800 p-6 rounded-lg max-w-4xl w-full mx-4 border border-gray-600">
      <h2 className="text-2xl font-bold mb-2 text-center text-yellow-300">Choose an Upgrade</h2>
      <p className="text-center text-gray-300 mb-6">Select one of the following upgrades to enhance your abilities</p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {upgradeChoices.map((upgrade, index) => (
          <div 
            key={index}
            className={`bg-gray-700 p-4 rounded-lg border-2 transition-all duration-200 hover:border-yellow-500 hover:bg-gray-600 cursor-pointer ${
              upgrade.category === 'attack' ? 'border-red-800' : 
              upgrade.category === 'defense' ? 'border-green-800' : 
              'border-blue-800'
            }`}
            onClick={() => selectUpgrade(upgrade)}
          >
            <div className="text-center mb-3">
              <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${
                upgrade.category === 'attack' ? 'bg-red-900/50' : 
                upgrade.category === 'defense' ? 'bg-green-900/50' : 
                'bg-blue-900/50'
              }`}>
                {upgrade.icon}
              </div>
            </div>
            <h3 className={`text-center font-semibold mb-2 ${
              upgrade.category === 'attack' ? 'text-red-400' : 
              upgrade.category === 'defense' ? 'text-green-400' : 
              'text-blue-400'
            }`}>
              {upgrade.name}
            </h3>
            <p className="text-sm text-gray-300 mb-3">{upgrade.description}</p>
            
            {/* Show current level if applicable */}
            {upgrades.filter(u => u.id === upgrade.id).length > 0 && (
              <div className="text-center text-xs bg-gray-800 rounded-full py-1 mb-2">
                Current Level: {upgrades.filter(u => u.id === upgrade.id).length} / {upgrade.maxLevel}
              </div>
            )}
            
            <div className="text-center text-xs text-gray-400">
              Category: {upgrade.category === 'attack' ? 'Attack' : upgrade.category === 'defense' ? 'Defense' : 'Utility'}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)}
		 
{isPaused && !showUpgradeScreen && (
  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-gray-800 p-8 rounded-lg shadow-lg text-center">
      <h2 className="text-3xl font-bold text-white mb-6">GAME PAUSED</h2>
      
      {/* Pause menu content */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        {/* ... */}
      </div>
      
      <div className="flex space-x-4">
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 flex-1 rounded-lg"
          onClick={togglePause}
        >
          Resume Game
        </button>
        <button
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 flex-1 rounded-lg"
          onClick={openTutorial}
        >
          How to Play
        </button>
      </div>
	  
    </div>
  </div>
)}

  
		  {/* HUD overlay - positioned at the top */}
          <div className="absolute top-0 left-0 right-0 z-40 bg-gray-800 bg-opacity-70 p-2">
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex flex-wrap items-center space-x-2 md:space-x-4">
                <div className="flex items-center bg-red-900/30 p-2 rounded-lg">
                  <Shield className="h-6 w-6 text-red-400 mr-2" />
                  <div className="w-24 md:w-32 h-4 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${healthPercent < 30 ? 'bg-red-600' : healthPercent < 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                      style={{ width: `${healthPercent}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center bg-yellow-900/30 p-2 rounded-lg">
                  <Target className="h-6 w-6 text-yellow-400 mr-2" />
                  <span className="text-yellow-300">{score}</span>
                </div>
                <div className="flex items-center bg-purple-900/30 p-2 rounded-lg">
                  <Zap className="h-6 w-6 text-purple-400 mr-2" />
                  <span className="text-purple-300">{threatsDestroyed}</span>
                </div>
                <div className="flex items-center bg-blue-900/30 p-2 rounded-lg">
                  <Activity className="h-6 w-6 text-blue-400 mr-2" />
                  <span className="text-blue-300">Level {level}</span>
                </div>
              </div>
              
			  <button 
  className="bg-yellow-600 px-3 py-1 rounded text-sm"
  onClick={() => navigate('/leaderboard')}
>
  Leaderboard
</button>
			  
              <div className="flex items-center space-x-2">
                <div className="hidden md:flex items-center bg-indigo-900/30 p-2 rounded-lg mr-2">
                  <Clock className="h-6 w-6 text-indigo-400 mr-2" />
                  <div className="w-20 h-4 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 transition-all duration-300" 
                      style={{ width: `${100 - specialCooldownPercent}%` }}
                    ></div>
                  </div>
                </div>
                
				<button 
  className={`px-3 py-1 rounded text-sm ${isPaused ? 'bg-green-600' : 'bg-blue-600'}`}
  onClick={togglePause}
>
  {isPaused ? 'Resume' : 'Pause'}
</button>
				
                <button 
                  className={`px-3 py-1 rounded text-sm ${autoFire ? 'bg-green-600' : 'bg-blue-600'}`}
                  onClick={toggleAutoFire}
                >
                  {autoFire ? 'Auto-Fire ON' : 'Auto-Fire OFF'}
                </button>
				  <button 
    className={`px-3 py-1 rounded text-sm ${soundEnabled ? 'bg-green-600' : 'bg-blue-600'}`}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleSound();
    }}
  >
    <div className="flex items-center">
      {soundEnabled ? (
        <>
          <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sound
        </>
      ) : (
        <>
          <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 9L16 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 9L22 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Muted
        </>
      )}
    </div>
  </button>
                <button 
                  className="bg-red-600 px-3 py-1 rounded text-sm"
                  onClick={resetGame}
                >
                  Reset
                </button>
                <button 
                  className="bg-purple-600 px-3 py-1 rounded text-sm"
                  onClick={openTutorial}
                >
                  Help
                </button>
              </div>
            </div>
            
			{upgrades.length > 0 && (
  <div className="mt-2">
    <div className="text-xs text-gray-400 mb-1">Active Upgrades:</div>
    <div className="flex flex-wrap gap-2">
      {Object.values(UPGRADES)
        // Get unique upgrades that the player has
        .filter(upgrade => upgrades.some(u => u.id === upgrade.id))
        .map(upgrade => {
          const count = upgrades.filter(u => u.id === upgrade.id).length;
          return (
            <div 
              key={upgrade.id} 
              className="flex items-center text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: upgrade.category === 'attack' ? 'rgba(220, 38, 38, 0.2)' : 
                                 upgrade.category === 'defense' ? 'rgba(74, 222, 128, 0.2)' : 
                                 'rgba(96, 165, 250, 0.2)',
                color: upgrade.category === 'attack' ? '#f87171' : 
                       upgrade.category === 'defense' ? '#4ade80' : 
                       '#60a5fa'
              }}
            >
              {upgrade.icon && <span className="mr-1">{upgrade.icon}</span>}
              <span>{upgrade.name}</span>
              {count > 1 && (
                <span className="ml-1 bg-gray-800 px-1 rounded-full text-xs">
                  {count}
                </span>
              )}
            </div>
          );
        })
      }
    </div>
  </div>
)}
			
            {/* Active Power-Ups */}
            {activePowerUpsRef.current.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {activePowerUpsRef.current.map(powerUp => (
                  <div 
                    key={powerUp.id} 
                    className="flex items-center text-xs px-2 py-1 rounded"
                    style={{ backgroundColor: `${powerUp.color}30` || '#6366f130', color: powerUp.color || '#a5b4fc' }}
                  >
                    {powerUp.icon && <span className="mr-1">{powerUp.icon}</span>}
                    <span>{powerUp.name}</span>
                    {powerUp.effect === 'shield' && powerUp.charges > 0 && (
                      <span className="ml-1 bg-gray-800 px-1 rounded-full text-xs">
                        {powerUp.charges}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Controls overlay - positioned at the bottom */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-40">
            <div className="bg-gray-800 bg-opacity-80 rounded-lg p-2 flex space-x-2">
 
              <button
                className={`${specialCooldown > 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white px-4 py-2 rounded flex items-center`}
                onClick={activateSpecialAbility}
                disabled={specialCooldown > 0}
              >
                <span>Special Ability (E)</span>
                {specialCooldown > 0 && (
                  <span className="ml-2 text-xs bg-gray-700 px-2 py-1 rounded">
                    {Math.ceil(specialCooldown / 1000)}s
                  </span>
                )}
              </button>
            </div>
          </div>
  
		  {/* In your game area */}
{/* Warning message */}
{warningMessage ? (
  <div 
    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
              bg-red-900/90 px-8 py-6 rounded-lg border-4 border-red-500 
              text-white text-center z-50 animate-pulse shadow-xl"
    style={{ 
      minWidth: "300px", 
      boxShadow: "0 0 30px rgba(220, 38, 38, 0.7), 0 0 15px #fff" 
    }}
  >
    <h3 className="text-2xl font-bold mb-2">{warningMessage}</h3>
    <p className="text-sm">Prepare to defend against the Botnet Master!</p>
     </div>
) : null}

{/* Boss health bar if active */}
{bossActiveRef.current && bossRef.current && (
  <div className="absolute top-2 left-0 right-0 mx-auto w-3/4 max-w-lg z-50">
    <div className="bg-gray-900/80 p-2 rounded-lg border border-red-600">
      <div className="flex items-center justify-between mb-1">
        <span className="text-red-400 font-bold">{bossRef.current.type}</span>
        <span className="text-xs text-gray-300">
          {Math.floor(bossRef.current.health)} / {bossRef.current.maxHealth}
        </span>
      </div>
      <div className="w-full h-4 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-red-600 transition-all duration-300"
          style={{ width: `${(bossRef.current.health / bossRef.current.maxHealth) * 100}%` }}
        ></div>
      </div>
    </div>
  </div>
)}
          		  
          {/* Game info overlay - smaller and positioned at bottom left */}
          <div className="absolute bottom-1 left-1 z-40 text-xs text-gray-400 opacity-60 hover:opacity-100 transition-opacity bg-gray-800 bg-opacity-50 p-1 rounded">
            <div className="flex space-x-4">
              <p>Threats: {threatsRef.current.length}</p>
              <p>Power-ups: {availablePowerUpsRef.current.length}</p>
              <p>Spawn: {Math.round(threatSpawnRateRef.current)}ms</p>
            </div>
          </div>
		  {/* Game Over */}
          {health <= 0 && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
              <div className="bg-gray-800 p-8 rounded-lg text-center">
                <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">Game Over</h2>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-700 p-4 rounded text-left">
                    <p className="text-gray-400 text-sm">Final Score</p>
                    <p className="text-yellow-300 text-2xl font-bold">{score}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded text-left">
                    <p className="text-gray-400 text-sm">Threats Destroyed</p>
                    <p className="text-purple-300 text-2xl font-bold">{threatsDestroyed}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded text-left">
                    <p className="text-gray-400 text-sm">Level Reached</p>
                    <p className="text-blue-300 text-2xl font-bold">{level}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded text-left">
                    <p className="text-gray-400 text-sm">Class</p>
                    <p className={`${PLAYER_CLASSES[selectedClass]?.textColor} text-xl font-bold`}>
                      {PLAYER_CLASSES[selectedClass]?.name}
                    </p>
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 flex-1 rounded-lg"
                    onClick={() => selectClass(selectedClass)}
                  >
                    Try Again
                  </button>
		<button
  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 flex-1 rounded-lg"
  onClick={() => setShowScoreSubmission(true)}
>
  Submit Score
</button>
				  
                  <button
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 flex-1 rounded-lg"
                    onClick={resetGame}
                  >
                    Change Class
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

{!gameActive && (
  <div className="flex-grow flex items-center justify-center p-4">
    <div className="bg-gray-800 p-6 rounded-lg w-full max-w-6xl grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3">
       <div className="flex justify-between items-center mb-4">
  <h2 className="text-xl font-bold text-center">Choose Your Specialist</h2>
  <div className="flex space-x-2">
    <button 
      className="bg-yellow-600 px-3 py-1 rounded text-sm hover:bg-yellow-700"
      onClick={() => navigate('/leaderboard')}
    >
      Leaderboard
    </button>
    <button 
      className="bg-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-700"
      onClick={openTutorial}
    >
      How to Play
    </button>
  </div>
</div>
{/* Show authentication status */}
{isAuthenticated && profile ? (
  <div className="mb-4 p-4 bg-green-900/30 border border-green-700 rounded-lg">
    <h3 className="font-semibold text-green-400 mb-1">Welcome back, {profile.username}!</h3>
    <p className="text-sm text-gray-300">
      Your scores will be automatically saved to the global leaderboard.
    </p>
  </div>
) : (
  <div className="mb-4 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
    <h3 className="font-semibold text-blue-400 mb-1">Want to compete on the leaderboard?</h3>
    <p className="text-sm text-gray-300">
      Use your "Shielded Onion" account to save your high scores to the global leaderboard.
    </p>
    <div className="mt-2 flex gap-2">
      <button onClick={() => navigate('/login')} className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white">
        Login
      </button>
      <button onClick={() => navigate('/register')} className="text-sm px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white">
        Register
      </button>
    </div>
  </div>
)}
		
                <h3 className="text-lg font-semibold text-center mb-4 text-white">Choose Your Player Class</h3>
		
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Your existing class selection code */}
          {Object.entries(PLAYER_CLASSES).map(([key, classData]) => (
            <div 
              key={key}
              className={`border border-gray-700 ${classData.bgColor} p-4 rounded-lg cursor-pointer hover:bg-opacity-70 hover:border-gray-500`}
              onClick={() => selectClass(key)}
            >
              {/* Existing class data display */}
              <div className="flex items-center">
                <div className={`w-10 h-10 flex items-center justify-center mr-2 rounded-full ${classData.bgColor}`}>
                  {classData.icon}
                </div>
                <h3 className={`text-lg font-semibold ${classData.textColor}`}>{classData.name}</h3>
              </div>
              <p className="text-sm text-gray-300 my-2">{classData.description}</p>
              <div className="text-xs text-gray-400 mb-2">
                Special: <span className={classData.textColor}>{classData.specialAbility}</span>
                <span className="ml-2 text-gray-500">(Cooldown: {classData.cooldown/1000}s)</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center">
                  <Shield size={12} className="mr-1 text-red-400" />
                  <span>Health: {classData.stats.health}</span>
                </div>
                <div className="flex items-center">
                  <Zap size={12} className="mr-1 text-yellow-400" />
                  <span>Damage: {classData.stats.damage}</span>
                </div>
                <div className="flex items-center">
                  <Rocket size={12} className="mr-1 text-green-400" />
                  <span>Speed: {classData.stats.speed}</span>
                </div>
                <div className="flex items-center">
                  <Target size={12} className="mr-1 text-blue-400" />
                  <span>Fire Rate: {classData.stats.fireRate}ms</span>
                </div>
                <div className="flex items-center col-span-2">
                  <Activity size={12} className="mr-1 text-purple-400" />
                  <span>Attack Range: {classData.range}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Threats Information Panel */}
      <div className="lg:col-span-1 bg-gray-900 rounded-lg p-4 overflow-y-auto" style={{ maxHeight: '600px' }}>
        <h3 className="text-lg font-bold mb-3 text-center text-red-400">Threat Database</h3>
        <p className="text-gray-300 text-sm mb-4">Familiarize yourself with the cyber threats you'll encounter:</p>
        
        <div className="space-y-3">
          {THREAT_TYPES.filter(threat => !threat.isSplit).map((threat, index) => (
            <div key={index} className={`${threat.bgColor} p-3 rounded-lg border border-gray-700`}>
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-full mr-2" style={{backgroundColor: threat.color}}>
                  {threat.icon}
                </div>
                <h4 className="font-bold" style={{color: threat.color}}>{threat.name}</h4>
              </div>
              
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <div className="text-gray-300">Health:</div>
                <div className="text-right">{threat.health}</div>
                
                <div className="text-gray-300">Damage:</div>
                <div className="text-right">{threat.damage}</div>
                
                <div className="text-gray-300">Resistance:</div>
                <div className="text-right">{threat.resistance ? `${threat.resistance * 100}%` : 'None'}</div>
                
                <div className="text-gray-300">Behavior:</div>
                <div className="text-right">
                  {threat.behavior === 'standard' ? 'Regular' :
                   threat.behavior === 'fast' ? 'Fast' :
                   threat.behavior === 'erratic' ? 'Erratic' :
                   threat.behavior === 'stealth' ? 'Stealth' :
                   threat.behavior === 'explosive' ? 'Explosive' :
                   threat.behavior === 'splitting' ? 'Splitting' : 'Unknown'}
                </div>
              </div>
              
              <div className="mt-2 text-xs text-gray-300">
                {threat.behavior === 'standard' ? 'Regular movement pattern toward player.' :
                 threat.behavior === 'fast' ? 'Moves faster than normal threats.' :
                 threat.behavior === 'erratic' ? 'Moves unpredictably and is hard to target.' :
                 threat.behavior === 'stealth' ? 'Invisible until close to player.' :
                 threat.behavior === 'explosive' ? 'Explodes on death, damaging nearby threats.' :
                 threat.behavior === 'splitting' ? 'Splits into smaller bugs when destroyed.' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)}
{/* Add the ScoreSubmissionModal here */}
<ScoreSubmissionModal
  isOpen={showScoreSubmission}
  onClose={() => setShowScoreSubmission(false)}
  gameStats={prepareGameStats()}
/>
    </div>
  );
};
  
export default CyberGame;